import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  FaBuilding,
  FaSearch,
  FaChevronDown,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCheck,
  FaExclamationTriangle,
  FaSyncAlt,
  FaTimes,
  FaCheckCircle,
  FaAdjust,
  FaClock,
  FaLock,
  FaUnlock,
  FaHistory,
  FaCalculator,
  FaDatabase,
  FaShieldAlt,
  FaInfoCircle,
  FaSpinner,
  FaIdBadge,
  FaCube,
  FaPlayCircle,
  FaPuzzlePiece,
  FaLayerGroup,
  FaHdd,
  FaChartBar,
  FaThList
} from 'react-icons/fa';

import { API_BASE_URL, getHeaders } from '../utils/api';
import * as meteringService from '../services/metering.service';
import telemetry from '../services/telemetry.service';
import './styles/Metering.css';

// --- Module Metadata for Telemetry ---
const MODULE_METADATA = {
  iap_admin: {
    title: 'Admin Dashboard Portal',
    workflow: 'Admin Portal Workflows',
    description: 'Admin portal management, organization rules & telemetry oversight',
    icon: FaShieldAlt,
    color: '#4f46e5',
    bg: '#e0e7ff'
  },
  content_studio: {
    title: 'Content Studio',
    workflow: 'Content Creator Extension Workflows',
    description: 'Content creator extension, walkthrough designer & flow authoring',
    icon: FaCube,
    color: '#0284c7',
    bg: '#e0f2fe'
  },
  player: {
    title: 'Website SDK Player',
    workflow: 'Website SDK Runtime Flows',
    description: 'Website SDK runtime engine & embedded flow step rendering',
    icon: FaPlayCircle,
    color: '#059669',
    bg: '#d1fae5'
  },
  sdk: {
    title: 'Website SDK Player',
    workflow: 'Website SDK Runtime Flows',
    description: 'Website SDK runtime engine & embedded flow step rendering',
    icon: FaPlayCircle,
    color: '#059669',
    bg: '#d1fae5'
  },
  extension_player: {
    title: 'Extension Player',
    workflow: 'End-User Browser Extension Runtime Flows',
    description: 'End-user browser extension runtime & client overlay execution',
    icon: FaPuzzlePiece,
    color: '#d97706',
    bg: '#fef3c7'
  }
};

const formatByteSize = (bytes) => {
  if (!bytes || isNaN(bytes) || Number(bytes) === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// --- Helper Functions for Retention Audit Trail ---
const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch (e) {
    return '—';
  }
};

const formatUtcDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { timeZone: 'UTC' });
  } catch (e) {
    return '—';
  }
};

const getDuration = (startStr, endStr) => {
  if (!startStr || !endStr) return '—';
  try {
    const s = new Date(startStr);
    const e = new Date(endStr);
    const diffMs = e - s;
    if (isNaN(diffMs) || diffMs < 0) return '—';
    if (diffMs < 1000) return `${diffMs}ms`;
    return `${(diffMs / 1000).toFixed(2)}s`;
  } catch (e) {
    return '—';
  }
};

const getRemainingDaysInfo = (effectiveToUtc, assignedAtUtc, effectiveFromUtc) => {
  if (!effectiveToUtc) {
    return {
      days: null,
      label: 'Lifetime License',
      shortLabel: 'Ongoing',
      badgeClass: 'license-badge-ongoing',
      status: 'ongoing',
      percentConsumed: 0,
      icon: FaShieldAlt,
      theme: {
        bg: '#eff6ff',
        border: '#bfdbfe',
        color: '#1d4ed8',
        dot: '#3b82f6'
      }
    };
  }

  const targetDate = new Date(effectiveToUtc);
  if (isNaN(targetDate.getTime())) return null;

  const now = new Date();

  const msPerDay = 1000 * 60 * 60 * 24;
  const targetDateOnly = Date.UTC(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  const nowDateOnly = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.floor((targetDateOnly - nowDateOnly) / msPerDay);

  let percentElapsed = 0;
  const startDateStr = assignedAtUtc || effectiveFromUtc;
  if (startDateStr) {
    const startDate = new Date(startDateStr);
    if (!isNaN(startDate.getTime()) && targetDate > startDate) {
      const totalDurationMs = targetDate.getTime() - startDate.getTime();
      const elapsedMs = Math.max(0, now.getTime() - startDate.getTime());
      percentElapsed = Math.min(100, Math.round((elapsedMs / totalDurationMs) * 100));
    }
  }

  if (diffDays < 0) {
    const daysAgo = Math.abs(diffDays);
    return {
      days: diffDays,
      label: `Expired (${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago)`,
      shortLabel: `Expired (${daysAgo}d)`,
      badgeClass: 'license-badge-expired',
      status: 'expired',
      percentConsumed: 100,
      icon: FaExclamationTriangle,
      theme: {
        bg: '#fef2f2',
        border: '#fca5a5',
        color: '#b91c1c',
        dot: '#ef4444'
      }
    };
  } else if (diffDays === 0) {
    return {
      days: 0,
      label: 'Expires today',
      shortLabel: 'Expires Today',
      badgeClass: 'license-badge-warning',
      status: 'warning',
      percentConsumed: 95,
      icon: FaClock,
      theme: {
        bg: '#fffbe6',
        border: '#ffe58f',
        color: '#d46b08',
        dot: '#fa8c16'
      }
    };
  } else if (diffDays <= 7) {
    return {
      days: diffDays,
      label: `${diffDays} ${diffDays === 1 ? 'day' : 'days'} left`,
      shortLabel: `${diffDays}d left`,
      badgeClass: 'license-badge-warning',
      status: 'warning',
      percentConsumed: percentElapsed,
      icon: FaClock,
      theme: {
        bg: '#fff7ed',
        border: '#fdba74',
        color: '#c2410c',
        dot: '#f97316'
      }
    };
  } else {
    return {
      days: diffDays,
      label: `${diffDays} ${diffDays === 1 ? 'day' : 'days'} left`,
      shortLabel: `${diffDays}d left`,
      badgeClass: 'license-badge-active',
      status: 'active',
      percentConsumed: percentElapsed,
      icon: FaCheckCircle,
      theme: {
        bg: '#ecfdf5',
        border: '#6ee7b7',
        color: '#047857',
        dot: '#10b981'
      }
    };
  }
};

const renderRetentionStatus = (status, failureReason) => {
  if (!status) return '—';
  let badgeClass = 'status-badge retired';
  let extraStyle = {};

  if (status.includes('Success') || status.includes('Completed') || status === 'ExecutedSuccessfully') {
    badgeClass = 'status-badge active';
  } else if (status === 'DryRun' || status.includes('DryRun')) {
    badgeClass = 'status-badge draft';
  } else if (status.includes('Fail') || status === 'Failed') {
    badgeClass = 'status-badge';
    extraStyle = { background: '#fee2e2', color: '#b91c1c' };
  }

  return (
    <span className={badgeClass} style={extraStyle} title={failureReason || undefined}>
      {status}
      {failureReason && <FaInfoCircle size={10} style={{ marginLeft: 4, cursor: 'help', verticalAlign: 'middle' }} />}
    </span>
  );
};

export default function Metering({ token, roles = [], orgId }) {
  const roleList = Array.isArray(roles) ? roles : [];
  const isSystemAdmin = roleList.some(r => typeof r === 'string' && r.toLowerCase() === 'systemadministrator');
  const isOrgAdmin = roleList.some(r => typeof r === 'string' && r.toLowerCase() === 'organizationadministrator');

  // Global States
  const [activeTab, setActiveTab] = useState(isSystemAdmin ? 'tiers' : 'assignments');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(() => {
    try {
      return orgId || localStorage.getItem('selected_organization_id') || '';
    } catch (e) {
      return orgId || '';
    }
  });
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [apiError, setApiError] = useState('');
  const [apiSuccess, setApiSuccess] = useState('');
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const orgDropdownRef = useRef(null);

  const fetchOrgsAbortRef = useRef(null);
  const tabContentSeqRef = useRef(0);

  useEffect(() => {
    return () => {
      fetchOrgsAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!isSystemAdmin && orgId && selectedOrgId !== orgId) {
      setSelectedOrgId(orgId);
    }
  }, [isSystemAdmin, orgId, selectedOrgId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target)) {
        setIsOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 2. Licensing Tiers State
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [savingTier, setSavingTier] = useState(false);
  const [showTierModal, setShowTierModal] = useState(false);
  const [showPlansOverviewModal, setShowPlansOverviewModal] = useState(false);
  const [editingTierKey, setEditingTierKey] = useState(null);
  const [capabilities, setCapabilities] = useState({ features: [], limits: [] });
  const [loadingCapabilities, setLoadingCapabilities] = useState(false);
  const [tierForm, setTierForm] = useState({
    tierKey: '',
    name: '',
    description: '',
    status: 'Draft',
    isDefaultTier: false,
    featurePolicies: [],
    limitPolicies: []
  });

  const capabilityMeta = useMemo(() => {
    const map = {};
    [...capabilities.features, ...capabilities.limits].forEach(c => {
      map[c.key] = c;
    });
    return map;
  }, [capabilities]);

  // 3. Org Assignment & Projections State
  const [entitlements, setEntitlements] = useState(null);
  const [usages, setUsages] = useState([]);
  const [, setEnforcementEvents] = useState([]);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);

  const displayTiers = React.useMemo(() => {
    if (!tiers || tiers.length === 0) return [];
    if (!entitlements || !entitlements.tierKey) return tiers;

    const hasActive = tiers.some(t => t.tierKey === entitlements.tierKey);
    if (hasActive) return tiers;

    const activeMapped = {
      tierKey: entitlements.tierKey,
      name: entitlements.tierName || entitlements.displayName || entitlements.tierKey,
      description: entitlements.description || "Active license plan assigned to your organization.",
      status: "Active",
      version: entitlements.version || 1,
      isDefaultTier: Boolean(entitlements.isDefaultTier),
      featurePolicies: (entitlements.enabledFeatures || []).map(fKey => ({
        featureKey: fKey,
        isEnabled: true,
        gracePeriodDays: 0
      })),
      limitPolicies: (entitlements.limits || []).map(l => ({
        limitKey: l.limitKey,
        value: l.value,
        unit: l.unit || 'count',
        softLimitPercentage: l.softLimitPercentage || 80,
        hardLimit: l.hardLimit || l.value,
        overageRate: l.overageRate || 0,
        currencyCode: l.currencyCode || 'USD'
      }))
    };

    return [activeMapped, ...tiers];
  }, [tiers, entitlements]);

  const allOverviewFeatureKeys = useMemo(() => {
    const set = new Set();
    (capabilities?.features || []).forEach(c => { if (c.key) set.add(c.key); });
    (displayTiers || []).forEach(t => {
      (t.featurePolicies || []).forEach(f => {
        if (f.featureKey) set.add(f.featureKey);
      });
    });
    return Array.from(set).sort((a, b) => {
      const nameA = capabilityMeta[a]?.displayName || a;
      const nameB = capabilityMeta[b]?.displayName || b;
      return nameA.localeCompare(nameB);
    });
  }, [capabilities, displayTiers, capabilityMeta]);

  const allOverviewLimitKeys = useMemo(() => {
    const set = new Set();
    (capabilities?.limits || []).forEach(c => { if (c.key) set.add(c.key); });
    (displayTiers || []).forEach(t => {
      (t.limitPolicies || []).forEach(l => {
        if (l.limitKey) set.add(l.limitKey);
      });
    });
    return Array.from(set).sort((a, b) => {
      const nameA = capabilityMeta[a]?.displayName || a;
      const nameB = capabilityMeta[b]?.displayName || b;
      return nameA.localeCompare(nameB);
    });
  }, [capabilities, displayTiers, capabilityMeta]);

  const isDataGovernanceEnabled = React.useMemo(() => {
    if (!entitlements || !Array.isArray(entitlements.enabledFeatures)) {
      return false;
    }
    return entitlements.enabledFeatures.includes('data_governance');
  }, [entitlements]);

  const isDataGovernanceEnabledRef = useRef(isDataGovernanceEnabled);
  useEffect(() => {
    isDataGovernanceEnabledRef.current = isDataGovernanceEnabled;
  }, [isDataGovernanceEnabled]);

  useEffect(() => {
    if (activeTab === 'retention' && !isDataGovernanceEnabled) {
      setActiveTab(isSystemAdmin ? 'tiers' : 'assignments');
    }
    if (activeTab === 'reconciliation' && !isSystemAdmin) {
      setActiveTab('assignments');
    }
  }, [activeTab, isDataGovernanceEnabled, isSystemAdmin]);

  const [assigningTier, setAssigningTier] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    tierKey: '',
    effectiveFromDate: new Date().toISOString().split('T')[0],
    effectiveToDate: '',
    reasonCode: 'upgrade',
    notes: ''
  });

  // 4. Metering & Billing Breakdown State
  const [meteringRecords, setMeteringRecords] = useState([]);
  const [dailyUsageData, setDailyUsageData] = useState([]);
  const [cycleBreakdown, setCycleBreakdown] = useState(null);
  const [billingSummary, setBillingSummary] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [finalizingBilling, setFinalizingBilling] = useState(false);
  const [finalizationDates, setFinalizationDates] = useState({
    startUtc: new Date(new Date().setDate(1)).toISOString().slice(0, 10), // start of month
    endUtc: new Date().toISOString().slice(0, 10)
  });
  const [adjustingRecord, setAdjustingRecord] = useState(null);
  const [adjustmentForm, setAdjustmentForm] = useState({
    quantityDelta: '',
    reasonCode: 'vendor-reconciliation',
    notes: ''
  });

  // 5. Data Retention & Holds State
  const [retentionPolicies, setRetentionPolicies] = useState([]);
  const [editedPolicies, setEditedPolicies] = useState({});
  const [newPolicyForm, setNewPolicyForm] = useState({
    policyKey: '',
    name: '',
    retentionDays: '30',
    maxDeleteBatchSize: '1000',
    dryRun: false,
    resourceType: 'TelemetryEvents'
  });
  const [creatingPolicy, setCreatingPolicy] = useState(false);
  const [legalHoldInfo, setLegalHoldInfo] = useState({ isActive: false });
  const [lastHoldResponse, setLastHoldResponse] = useState(null);
  const [executionHistory, setExecutionHistory] = useState([]);
  const [executingRetention, setExecutingRetention] = useState({});
  const [savingRetentionPolicy, setSavingRetentionPolicy] = useState({});
  const [legalHoldForm, setLegalHoldForm] = useState({
    reason: '',
    scopeType: 'organization',
    scopeKey: ''
  });
  const [legalHoldReleaseForm, setLegalHoldReleaseForm] = useState({
    releasedBy: '',
    reason: ''
  });

  // 6. Reconciliation State
  const [reconciliationData, setReconciliationData] = useState(null);
  const [loadingReconciliation, setLoadingReconciliation] = useState(false);
  const [runningReconciliation, setRunningReconciliation] = useState(false);
  const [showRunReconcileConfirm, setShowRunReconcileConfirm] = useState(false);
  const [crossTenantSummaries, setCrossTenantSummaries] = useState(null);
  const [loadingCrossTenantSummaries, setLoadingCrossTenantSummaries] = useState(false);
  const [summaryFromUtc, setSummaryFromUtc] = useState('');
  const [summaryToUtc, setSummaryToUtc] = useState('');

  const handleFetchCrossTenantSummaries = useCallback(async (fromUtc, toUtc) => {
    if (!isSystemAdmin) return;
    setLoadingCrossTenantSummaries(true);
    try {
      const data = await meteringService.getCrossTenantSummaries(fromUtc, toUtc);
      setCrossTenantSummaries(data);
    } catch (err) {
      console.warn('Failed to load cross-tenant summaries:', err);
    } finally {
      setLoadingCrossTenantSummaries(false);
    }
  }, [isSystemAdmin]);

  // Telemetry States
  const [lastFlushTime, setLastFlushTime] = useState('Never');
  const [lastFlushStatus, setLastFlushStatus] = useState('None');
  const [selectedStatusModuleKey, setSelectedStatusModuleKey] = useState('iap_admin');
  const [moduleUsages, setModuleUsages] = useState([]);
  const [loadingModuleUsages, setLoadingModuleUsages] = useState(false);



  // Monitor telemetry queue state
  useEffect(() => {
    const updateQueue = () => {
      setLastFlushTime(telemetry.lastFlushTime || 'Never');
      setLastFlushStatus(telemetry.lastFlushStatus || 'None');
    };

    updateQueue();
    // Poll every 1 second for instant visual updates
    const id = setInterval(updateQueue, 1000);
    return () => clearInterval(id);
  }, []);



  // Fetch initial organizations
  const fetchOrgs = useCallback(async () => {
    fetchOrgsAbortRef.current?.abort();
    const controller = new AbortController();
    fetchOrgsAbortRef.current = controller;

    setLoadingOrgs(true);
    try {
      if (isOrgAdmin && orgId) {
        const targetOrg = orgId;
        const res = await fetch(`${API_BASE_URL}/organizations/${targetOrg}`, {
          headers: getHeaders(token),
          signal: controller.signal
        });
        if (res.ok) {
          const org = await res.json().catch(() => null);
          setOrganizations(org ? [org] : []);
        }
      } else {
        const res = await fetch(`${API_BASE_URL}/organizations`, {
          headers: getHeaders(token),
          signal: controller.signal
        });
        if (res.ok) {
          const data = await res.json().catch(() => []);
          const orgList = Array.isArray(data) ? data : [];
          setOrganizations(orgList);
          setSelectedOrgId((prev) => {
            if (prev && !orgList.some(o => String(o.id) === String(prev))) {
              try {
                localStorage.removeItem('selected_organization_id');
              } catch (e) { }
              return '';
            }
            return prev;
          });
        }
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.warn('Failed to load organizations from API, using default sandbox items.');
    } finally {
      if (fetchOrgsAbortRef.current === controller) {
        setLoadingOrgs(false);
      }
    }
  }, [token, isOrgAdmin, orgId]);

  // Trigger loading organizations on start
  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  // Fetch entitlements for the active organization globally
  useEffect(() => {
    if (!selectedOrgId) {
      setEntitlements(null);
      return;
    }
    let isCurrent = true;
    const fetchOrgEntitlements = async () => {
      try {
        const entData = await meteringService.getEntitlements(selectedOrgId);
        if (isCurrent) {
          setEntitlements(entData);
        }
      } catch (err) {
        console.warn('Failed to load entitlements globally for org:', selectedOrgId);
      }
    };
    fetchOrgEntitlements();
    return () => {
      isCurrent = false;
    };
  }, [selectedOrgId]);

  const fetchAndAdjustUsages = useCallback(async (orgId, rawUsages) => {
    try {
      const res = await fetch(`${API_BASE_URL}/users/organizations/${orgId}`, { headers: getHeaders(token) });
      if (res.ok) {
        const users = await res.json();
        const list = Array.isArray(users) ? users : [];
        const activeCount = list.filter(u =>
          String(u?.approvalStatus || '').trim().toLowerCase() === 'approved' && u.isActive
        ).length;

        const usagesList = Array.isArray(rawUsages) ? rawUsages : [];
        let hasUserLimit = false;
        const mapped = usagesList.map(u => {
          if (u.limitKey === 'max_users_per_org' || u.limitKey === 'business_active_users') {
            hasUserLimit = true;
            return { ...u, consumed: activeCount };
          }
          return u;
        });

        if (!hasUserLimit) {
          mapped.push({ limitKey: 'max_users_per_org', consumed: activeCount });
        }
        return mapped;
      }
    } catch (e) {
      console.warn('Failed to load active users for usage adjustment:', e);
    }
    return Array.isArray(rawUsages) ? rawUsages : [];
  }, [token]);

  // ==========================================
  // Tab change loading dispatcher
  // ==========================================
  const loadTabContent = useCallback(async () => {
    const seq = ++tabContentSeqRef.current;
    setApiError('');
    switch (activeTab) {


      case 'tiers':
        setLoadingTiers(true);
        try {
          const tiersList = await meteringService.getLicensingTiers();
          if (tabContentSeqRef.current === seq) {
            setTiers(tiersList);
          }
        } catch (err) {
          if (tabContentSeqRef.current === seq) {
            setApiError('Failed to load licensing tiers.');
          }
        } finally {
          if (tabContentSeqRef.current === seq) {
            setLoadingTiers(false);
          }
        }
        break;

      case 'assignments':
        if (isSystemAdmin) {
          try {
            const tiersList = await meteringService.getLicensingTiers();
            if (tabContentSeqRef.current === seq) {
              setTiers(tiersList || []);
            }
          } catch (err) {
            console.warn('Failed to load licensing tiers:', err);
          }
        }
        if (!selectedOrgId) {
          if (tabContentSeqRef.current === seq) {
            setAssignmentHistory([]);
            setCurrentAssignment(null);
            setUsages([]);
            setEnforcementEvents([]);
          }
          break;
        }
        try {
          const entData = await meteringService.getEntitlements(selectedOrgId);
          if (tabContentSeqRef.current !== seq) return;
          setEntitlements(entData);
          const [historyData, currentData] = await Promise.all([
            meteringService.getLicensingAssignments(selectedOrgId),
            entData?.currentAssignment ? Promise.resolve(entData.currentAssignment) : meteringService.getCurrentLicensingAssignment(selectedOrgId),
          ]);
          if (tabContentSeqRef.current !== seq) return;
          setAssignmentHistory(Array.isArray(historyData) ? historyData : []);
          setCurrentAssignment(currentData || null);
          const usageData = await meteringService.getCurrentUsages(selectedOrgId);
          if (tabContentSeqRef.current !== seq) return;
          const rawUsages = Array.isArray(usageData) ? usageData : (usageData?.usages || []);
          const adjustedUsages = await fetchAndAdjustUsages(selectedOrgId, rawUsages);
          if (tabContentSeqRef.current !== seq) return;
          setUsages(adjustedUsages);
          const events = await meteringService.getEnforcementEvents(selectedOrgId);
          if (tabContentSeqRef.current !== seq) return;
          setEnforcementEvents(events || []);
        } catch (err) {
          if (tabContentSeqRef.current === seq) {
            setApiError('Failed to load organization license assignment details.');
          }
        }
        break;

      case 'billing':
        if (!selectedOrgId) {
          if (tabContentSeqRef.current === seq) {
            setMeteringRecords([]);
            setBillingSummary(null);
            setDailyUsageData([]);
            setCycleBreakdown(null);
            setModuleUsages([]);
          }
          return;
        }
        setLoadingBilling(true);
        setLoadingModuleUsages(true);
        try {
          const summary = await meteringService.getMeteringSummary(selectedOrgId);
          if (tabContentSeqRef.current !== seq) return;
          if (summary && !Array.isArray(summary)) {
            setMeteringRecords(summary.records || []);
            setBillingSummary(summary);
          } else {
            setMeteringRecords(summary || []);
            setBillingSummary({
              records: summary || [],
              tierKey: '',
              billingCycleStartUtc: '',
              billingCycleEndUtc: ''
            });
          }
          const daily = await meteringService.getMeteringDaily(selectedOrgId);
          if (tabContentSeqRef.current !== seq) return;
          setDailyUsageData(daily);
          const breakdown = await meteringService.getMeteringCycleBreakdown(selectedOrgId);
          if (tabContentSeqRef.current !== seq) return;
          setCycleBreakdown(breakdown);

          // Load module usage telemetry for billing tab
          try {
            const modUsageData = await meteringService.getModuleUsage(selectedOrgId);
            if (tabContentSeqRef.current === seq) {
              setModuleUsages(Array.isArray(modUsageData) ? modUsageData : []);
            }
          } catch (errMod) {
            console.warn('Failed to load module usage telemetry in billing tab:', errMod);
          }
        } catch (err) {
          if (tabContentSeqRef.current === seq) {
            setApiError('Failed to load billing metrics.');
          }
        } finally {
          if (tabContentSeqRef.current === seq) {
            setLoadingBilling(false);
            setLoadingModuleUsages(false);
          }
        }
        break;

      case 'reconciliation':
        if (!isSystemAdmin) {
          break;
        }
        if (!selectedOrgId) {
          if (tabContentSeqRef.current === seq) {
            setReconciliationData(null);
          }
        } else {
          setLoadingReconciliation(true);
          try {
            const recData = await meteringService.getReconciliationStatus(selectedOrgId);
            if (tabContentSeqRef.current !== seq) return;
            setReconciliationData(recData);
          } catch (err) {
            console.warn('Failed to load reconciliation status:', err);
            if (tabContentSeqRef.current === seq) {
              setApiError('Failed to load reconciliation status.');
            }
          } finally {
            if (tabContentSeqRef.current === seq) {
              setLoadingReconciliation(false);
            }
          }
        }

        if (isSystemAdmin) {
          setLoadingCrossTenantSummaries(true);
          try {
            const ctData = await meteringService.getCrossTenantSummaries();
            if (tabContentSeqRef.current !== seq) return;
            setCrossTenantSummaries(ctData);
          } catch (err) {
            console.warn('Failed to load cross-tenant summaries:', err);
          } finally {
            if (tabContentSeqRef.current === seq) {
              setLoadingCrossTenantSummaries(false);
            }
          }
        }
        break;

      case 'retention':
        if (isSystemAdmin && isDataGovernanceEnabledRef.current) {
          try {
            const policies = await meteringService.getRetentionPolicies();
            if (tabContentSeqRef.current !== seq) return;
            setRetentionPolicies(policies);
          } catch (err) {
            console.warn('Failed to load retention policies:', err);
            if (tabContentSeqRef.current === seq) {
              setApiError('Failed to load retention policies.');
            }
          }

          try {
            const history = await meteringService.getRetentionExecutionHistory();
            if (tabContentSeqRef.current !== seq) return;
            setExecutionHistory(history);
          } catch (err) {
            console.warn('Failed to load retention execution history:', err);
            if (tabContentSeqRef.current === seq) {
              setApiError('Failed to load retention history.');
            }
          }

          if (selectedOrgId) {
            try {
              const hold = await meteringService.getLegalHold(selectedOrgId);
              if (tabContentSeqRef.current !== seq) return;
              setLegalHoldInfo(hold);
              setLastHoldResponse(null);
            } catch (err) {
              console.warn('Failed to load legal hold info:', err);
              if (tabContentSeqRef.current === seq) {
                setApiError('Failed to load legal hold settings.');
              }
            }
          } else {
            if (tabContentSeqRef.current === seq) {
              setLegalHoldInfo({ isActive: false });
              setLastHoldResponse(null);
            }
          }
        } else {
          if (tabContentSeqRef.current === seq) {
            setRetentionPolicies([]);
            setExecutionHistory([]);
            setLegalHoldInfo({ isActive: false });
            setLastHoldResponse(null);
          }
        }
        break;

      case 'telemetry':
        if (!selectedOrgId) {
          if (tabContentSeqRef.current === seq) {
            setUsages([]);
          }
          return;
        }
        try {
          try {
            const tiersList = await meteringService.getLicensingTiers();
            if (tabContentSeqRef.current !== seq) return;
            setTiers(tiersList || []);
          } catch (errTiers) {
            console.warn('Failed to load tiers in telemetry tab:', errTiers);
          }

          const entData = await meteringService.getEntitlements(selectedOrgId);
          if (tabContentSeqRef.current !== seq) return;
          setEntitlements(entData);
          const usageData = await meteringService.getCurrentUsages(selectedOrgId);
          if (tabContentSeqRef.current !== seq) return;
          const rawUsages = Array.isArray(usageData) ? usageData : (usageData?.usages || []);
          const adjustedUsages = await fetchAndAdjustUsages(selectedOrgId, rawUsages);
          if (tabContentSeqRef.current !== seq) return;
          setUsages(adjustedUsages);
        } catch (err) {
          console.warn('Failed to load telemetry entitlements/usages:', err);
        }
        break;

      default:
        break;
    }
  }, [activeTab, selectedOrgId, fetchAndAdjustUsages, isSystemAdmin]);

  useEffect(() => {
    loadTabContent();
  }, [loadTabContent]);

  useEffect(() => {
    if (!isSystemAdmin) {
      setLoadingCapabilities(false);
      return;
    }
    const loadCapabilities = async () => {
      setLoadingCapabilities(true);
      try {
        const data = await meteringService.getLicensingCapabilities();
        const caps = Array.isArray(data?.capabilities) ? data.capabilities : [];
        setCapabilities({
          features: caps.filter(c => c.kind === 'Feature'),
          limits: caps.filter(c => c.kind === 'Limit'),
        });
      } catch (err) {
        console.warn('[capabilities] Could not load global capabilities:', err);
      } finally {
        setLoadingCapabilities(false);
      }
    };
    loadCapabilities();
  }, [isSystemAdmin]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showTierModal && !savingTier) {
          setShowTierModal(false);
        }
        if (showPlansOverviewModal) {
          setShowPlansOverviewModal(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showTierModal, savingTier, showPlansOverviewModal]);

  useEffect(() => {
    let timer;
    if (apiError || apiSuccess) {
      timer = setTimeout(() => {
        setApiError('');
        setApiSuccess('');
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [apiError, apiSuccess]);

  const buildDefaultFeaturePolicies = (features) =>
    features.map(f => ({
      featureKey: f.key,
      isEnabled: false,
      gracePeriodDays: 7,
    }));

  const buildDefaultLimitPolicies = (limits) =>
    limits.map(l => ({
      limitKey: l.key,
      value: 0,
      unit: l.unit || 'count',
      softLimitPercentage: 50,
      hardLimit: 0,
      overageRate: 0,
      currencyCode: 'USD',
    }));

  // ==========================================
  // 2. Tiers CRUD Handlers
  // ==========================================
  const handleOpenNewTierModal = () => {
    setEditingTierKey(null);
    setTierForm({
      tierKey: '',
      name: '',
      description: '',
      status: 'Active',
      isDefaultTier: false,
      featurePolicies: buildDefaultFeaturePolicies(capabilities.features),
      limitPolicies: buildDefaultLimitPolicies(capabilities.limits),
    });
    setShowTierModal(true);
  };

  const handleOpenEditTier = (tier) => {
    setEditingTierKey(tier.tierKey);
    setTierForm({
      tierKey: tier.tierKey,
      name: tier.name,
      description: tier.description,
      status: tier.status,
      isDefaultTier: tier.isDefaultTier || false,
      featurePolicies: [...tier.featurePolicies],
      limitPolicies: [...tier.limitPolicies]
    });
    setShowTierModal(true);
  };

  const handleSaveTier = async (e) => {
    e.preventDefault();
    setApiError('');
    setApiSuccess('');

    // Programmatic Validation
    if (!tierForm.tierKey || !tierForm.tierKey.trim()) {
      const errMsg = 'Validation Error: Tier Key is required.';
      console.error(errMsg);
      setApiError(errMsg);
      return;
    }
    if (!tierForm.name || !tierForm.name.trim()) {
      const errMsg = 'Validation Error: Display Name is required.';
      console.error(errMsg);
      setApiError(errMsg);
      return;
    }
    if (!tierForm.description || !tierForm.description.trim()) {
      const errMsg = 'Validation Error: Commercial Description is required.';
      console.error(errMsg);
      setApiError(errMsg);
      return;
    }

    setSavingTier(true);

    try {
      if (editingTierKey) {
        await meteringService.updateLicensingTier(editingTierKey, tierForm);
        if (tierForm.status) {
          await meteringService.updateLicensingTierStatus(editingTierKey, tierForm.status);
        }
        setApiSuccess(`Tier '${tierForm.name}' updated successfully.`);
      } else {
        await meteringService.createLicensingTier(tierForm);
        const newTierKey = tierForm.tierKey;
        if (tierForm.status) {
          await meteringService.updateLicensingTierStatus(newTierKey, tierForm.status);
        }
        setApiSuccess(`Tier '${tierForm.name}' created successfully.`);
      }
      setShowTierModal(false);
      const list = await meteringService.getLicensingTiers();
      setTiers(list);
    } catch (err) {
      console.error('[handleSaveTier] Error during saving tier:', err);
      setApiError(err.message || 'Failed to save tier.');
    } finally {
      setSavingTier(false);
    }
  };

  const handleDeleteTier = async (key) => {
    if (!window.confirm(`Are you sure you want to delete tier '${key}'?`)) return;
    setApiError('');
    setApiSuccess('');
    try {
      await meteringService.deleteLicensingTier(key);
      setApiSuccess('Tier deleted successfully.');

      setTiers(prevTiers => prevTiers.filter(tier => tier.tierKey !== key));

    } catch (err) {
      setApiError(err.message || 'Failed to delete tier.');
    }

  };

  const handleFeatureToggle = (key) => {
    const list = tierForm.featurePolicies.map(f => {
      if (f.featureKey === key) {
        return { ...f, isEnabled: !f.isEnabled };
      }
      return f;
    });
    setTierForm({ ...tierForm, featurePolicies: list });
  };

  const handleLimitPolicyChange = (key, field, val) => {
    let finalVal = isNaN(val) ? 0 : Math.max(0, val);
    if (field === 'softLimitPercentage') {
      finalVal = Math.min(100, finalVal);
    }
    const list = tierForm.limitPolicies.map(l => {
      if (l.limitKey === key) {
        return { ...l, [field]: finalVal };
      }
      return l;
    });
    setTierForm({ ...tierForm, limitPolicies: list });
  };

  // ==========================================
  // 3. Org Assignment & Projections Handlers
  // ==========================================
  const handleAssignTierSubmit = async (e) => {
    e.preventDefault();
    setAssigningTier(true);
    setApiError('');
    setApiSuccess('');
    try {
      if (entitlements) {
        localStorage.setItem('iap_previous_license_' + selectedOrgId, JSON.stringify({
          tierKey: entitlements.tierKey,
          tierName: entitlements.tierName,
          assignedAtUtc: entitlements.assignedAtUtc,
          effectiveToUtc: entitlements.effectiveToUtc
        }));
      }

      const todayStr = new Date().toISOString().split('T')[0];
      const fromUtc = (assignmentForm.effectiveFromDate === todayStr)
        ? new Date(Date.now() + 60 * 1000).toISOString() // +60s buffer for network latency
        : `${assignmentForm.effectiveFromDate}T00:00:00.000Z`;

      const toUtc = !assignmentForm.effectiveToDate
        ? null
        : `${assignmentForm.effectiveToDate}T23:59:59.999Z`;

      await meteringService.createLicensingAssignment(selectedOrgId, {
        tierKey: assignmentForm.tierKey,
        effectiveFromUtc: fromUtc,
        effectiveToUtc: toUtc,
        reasonCode: assignmentForm.reasonCode,
        notes: assignmentForm.notes,
        assignedBy: 'Systems Administrator'
      });
      setApiSuccess(`Assigned new tier successfully.`);
      const entData = await meteringService.getEntitlements(selectedOrgId);
      setEntitlements(entData);
      const [historyData, currentData] = await Promise.all([
        meteringService.getLicensingAssignments(selectedOrgId),
        meteringService.getCurrentLicensingAssignment(selectedOrgId),
      ]);
      setAssignmentHistory(Array.isArray(historyData) ? historyData : []);
      setCurrentAssignment(currentData || null);
      const usageData = await meteringService.getCurrentUsages(selectedOrgId);
      const rawUsages = Array.isArray(usageData) ? usageData : (usageData?.usages || []);
      const adjustedUsages = await fetchAndAdjustUsages(selectedOrgId, rawUsages);
      setUsages(adjustedUsages);
      const events = await meteringService.getEnforcementEvents(selectedOrgId);
      setEnforcementEvents(events);
      // Reload billing breakdown
      const breakdown = await meteringService.getMeteringCycleBreakdown(selectedOrgId);
      setCycleBreakdown(breakdown);
    } catch (err) {
      setApiError(err.message || 'Failed to assign tier.');
    } finally {
      setAssigningTier(false);
    }
  };

  // ==========================================
  // 4. Billing finalization & Adjustment Handlers
  // ==========================================
  const handleFinalizeCycle = async () => {
    if (!window.confirm('Are you sure you want to finalize the selected billing cycle? Finalized records cannot be modified.')) return;
    setFinalizingBilling(true);
    setApiError('');
    setApiSuccess('');
    try {
      await meteringService.finalizeBillingCycle(selectedOrgId, {
        billingCycleStartUtc: new Date(finalizationDates.startUtc).toISOString(),
        billingCycleEndUtc: new Date(finalizationDates.endUtc).toISOString()
      });
      setApiSuccess('Billing cycle finalized. Records marked immutable.');
      const summary = await meteringService.getMeteringSummary(selectedOrgId);
      if (summary && !Array.isArray(summary)) {
        setMeteringRecords(summary.records || []);
        setBillingSummary(summary);
      } else {
        setMeteringRecords(summary || []);
      }
    } catch (err) {
      setApiError(err.message || 'Finalization failed.');
    } finally {
      setFinalizingBilling(false);
    }
  };

  const handleAdjustClick = (rec) => {
    if (rec.isFinalized) {
      alert('Finalized records cannot be adjusted.');
      return;
    }
    setAdjustingRecord(rec);
    setAdjustmentForm({
      quantityDelta: '',
      reasonCode: 'vendor-reconciliation',
      notes: ''
    });
  };

  const handleSaveAdjustment = async (e) => {
    e.preventDefault();
    setApiError('');
    setApiSuccess('');
    try {
      await meteringService.adjustMetering(selectedOrgId, {
        meteringRecordId: adjustingRecord.id,
        quantityDelta: Number(adjustmentForm.quantityDelta),
        reasonCode: adjustmentForm.reasonCode,
        notes: adjustmentForm.notes
      });
      setApiSuccess('Manual adjustment applied successfully.');
      setAdjustingRecord(null);
      // Reload billing state
      const summary = await meteringService.getMeteringSummary(selectedOrgId);
      if (summary && !Array.isArray(summary)) {
        setMeteringRecords(summary.records || []);
        setBillingSummary(summary);
      } else {
        setMeteringRecords(summary || []);
      }
      const breakdown = await meteringService.getMeteringCycleBreakdown(selectedOrgId);
      setCycleBreakdown(breakdown);
    } catch (err) {
      setApiError(err.message || 'Failed to save adjustment.');
    }
  };

  const handleTriggerReconciliation = async () => {
    if (!isSystemAdmin) return;
    setShowRunReconcileConfirm(false);
    setRunningReconciliation(true);
    setApiError('');
    setApiSuccess('');
    try {
      const payload = selectedOrgId ? { organizationId: selectedOrgId } : {};
      const result = await meteringService.runReconciliation(payload);
      if (result && result.partialFailure !== true) {
        const msg = result.runKey
          ? `Policy reconciliation run '${result.runKey}' executed successfully. Organizations evaluated: ${result.organizationsEvaluated || 1}, Drift count: ${result.organizationsWithDrift || 0}.`
          : (result.driftSummary || 'System policy reconciliation executed successfully across all tenants.');
        setApiSuccess(msg);
        if (selectedOrgId) {
          const updated = await meteringService.getReconciliationStatus(selectedOrgId);
          setReconciliationData(updated);
        }
        if (isSystemAdmin) {
          const updatedCt = await meteringService.getCrossTenantSummaries();
          setCrossTenantSummaries(updatedCt);
        }
      } else {
        setApiError('Reconciliation run completed with partial failure or drift exceptions.');
      }
    } catch (err) {
      console.error('Reconciliation error:', err);
      setApiError(err.message || 'Failed to trigger policy reconciliation.');
    } finally {
      setRunningReconciliation(false);
    }
  };

  // ==========================================
  // 5. Data Retention & Legal Hold Handlers
  // ==========================================
  const handleSaveRetentionPolicy = async (key, days, dryRun, maxDeleteBatchSize) => {
    if (!isSystemAdmin) return;
    setSavingRetentionPolicy({ ...savingRetentionPolicy, [key]: true });
    setApiError('');
    setApiSuccess('');
    try {
      await meteringService.updateRetentionPolicy(key, {
        retentionDays: Number(days),
        dryRun: Boolean(dryRun),
        maxDeleteBatchSize: Number(maxDeleteBatchSize)
      });
      setApiSuccess(`Retention policy '${key}' saved.`);
      const policies = await meteringService.getRetentionPolicies();
      setRetentionPolicies(policies);
      // Clear local edits for this policy
      setEditedPolicies(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    } catch (err) {
      setApiError(err.message || 'Failed to update policy.');
    } finally {
      setSavingRetentionPolicy({ ...savingRetentionPolicy, [key]: false });
    }
  };

  const handleCreateRetentionPolicy = async (e) => {
    e.preventDefault();
    if (!isSystemAdmin) return;

    const key = newPolicyForm.policyKey.trim();
    if (retentionPolicies.some(p => p.policyKey.toLowerCase() === key.toLowerCase())) {
      alert(`Validation Error: A retention policy with key '${key}' already exists.`);
      return;
    }

    setCreatingPolicy(true);
    setApiError('');
    setApiSuccess('');
    try {
      await meteringService.createRetentionPolicy({
        policyKey: newPolicyForm.policyKey.trim(),
        name: newPolicyForm.name.trim(),
        retentionDays: Number(newPolicyForm.retentionDays),
        maxDeleteBatchSize: Number(newPolicyForm.maxDeleteBatchSize),
        dryRun: Boolean(newPolicyForm.dryRun),
        resourceType: newPolicyForm.resourceType.trim()
      });
      setApiSuccess(`Retention policy '${newPolicyForm.policyKey}' created successfully.`);
      setNewPolicyForm({
        policyKey: '',
        name: '',
        retentionDays: '30',
        maxDeleteBatchSize: '1000',
        dryRun: false,
        resourceType: 'TelemetryEvents'
      });
      const policies = await meteringService.getRetentionPolicies();
      setRetentionPolicies(policies);
    } catch (err) {
      setApiError(err.message || 'Failed to create retention policy.');
    } finally {
      setCreatingPolicy(false);
    }
  };

  const handleRunRetention = async (key) => {
    if (!isSystemAdmin) return;
    setExecutingRetention({ ...executingRetention, [key]: true });
    setApiError('');
    setApiSuccess('');
    try {
      const res = await meteringService.runRetentionExecution(key);
      setApiSuccess(`Retention run completed. Status: ${res.status}. Deleted: ${res.itemsDeleted}. Blocked by legal hold: ${res.itemsBlockedByLegalHold}`);
      const history = await meteringService.getRetentionExecutionHistory();
      setExecutionHistory(history);
    } catch (err) {
      setApiError(err.message || 'Failed to trigger retention.');
    } finally {
      setExecutingRetention({ ...executingRetention, [key]: false });
    }
  };

  const handlePlaceLegalHold = async (e) => {
    e.preventDefault();
    setApiError('');
    setApiSuccess('');
    try {
      const response = await meteringService.placeLegalHold(selectedOrgId, {
        reason: legalHoldForm.reason,
        scopeType: legalHoldForm.scopeType,
        scopeKey: legalHoldForm.scopeKey
      });
      setLastHoldResponse(response);
      setApiSuccess('Legal hold successfully placed on organization.');
      const hold = await meteringService.getLegalHold(selectedOrgId);
      setLegalHoldInfo(hold);
      setLegalHoldForm({ reason: '', scopeType: 'organization', scopeKey: '' });
    } catch (err) {
      setApiError(err.message || 'Failed to place hold.');
    }
  };

  const handleReleaseLegalHold = async (e) => {
    e.preventDefault();
    setApiError('');
    setApiSuccess('');
    try {
      await meteringService.releaseLegalHold(selectedOrgId, {
        releasedBy: legalHoldReleaseForm.releasedBy,
        reason: legalHoldReleaseForm.reason
      }, legalHoldInfo.id || legalHoldInfo.legalHoldId);
      setApiSuccess('Legal hold successfully released.');
      const hold = await meteringService.getLegalHold(selectedOrgId);
      setLegalHoldInfo(hold);
      setLegalHoldReleaseForm({ releasedBy: '', reason: '' });
      setLastHoldResponse(null);
    } catch (err) {
      setApiError(err.message || 'Failed to release hold.');
    }
  };

  // Format Helper for byte units
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getLimitValDisplay = (key, value) => {
    if (key === 'storage') {
      return formatBytes(value);
    }
    return value.toLocaleString();
  };

  const getTableColumns = () => {
    const columns = [];
    const keys = new Set();
    dailyUsageData.forEach(day => {
      if (!day) return;
      Object.keys(day).forEach(key => {
        if (key !== 'date') {
          keys.add(key);
        }
      });
    });

    const preferredOrder = ['consumerSessions', 'eventsIngested', 'apiRequests', 'storageBytes', 'featureUsage'];
    preferredOrder.forEach(key => {
      if (keys.has(key)) {
        columns.push(key);
        keys.delete(key);
      }
    });

    keys.forEach(key => {
      columns.push(key);
    });

    return columns;
  };

  const formatHeader = (key) => {
    if (key === 'storageBytes') return 'Storage';
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
  };

  const formatCellValue = (key, value) => {
    if (value === undefined || value === null) return '—';
    if (key === 'storageBytes') return formatBytes(value);
    return Number(value).toLocaleString();
  };

  return (
    <div className="metering-wrapper">
      <div className="metering-container">

        {/* Connection Mode Bar */}
        <div className="metering-header">
          <div className="metering-header-left">
            <div className="metering-header-icon">
              <FaCalculator size={22} />
            </div>
            <div className="metering-header-title">
              <h1>Telemetry, Metering & Licensing</h1>
              <p>Platform billing control, proration, overrides, and legal holds logs.</p>
            </div>
          </div>
        </div>

        {/* Global Notifications */}
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 10000, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 400 }}>
          {apiError && (
            <div className="enforcement-alert-item danger" style={{ margin: 0, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
              <FaExclamationTriangle className="enforcement-alert-icon" />
              <div className="enforcement-alert-content">
                <div className="enforcement-alert-title">Operation Warning</div>
                <div className="enforcement-alert-desc">{apiError}</div>
              </div>
              <button className="text-red-700 bg-transparent border-none cursor-pointer" onClick={() => setApiError('')}><FaTimes /></button>
            </div>
          )}

          {apiSuccess && (
            <div className="enforcement-alert-item" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46', margin: 0, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
              <FaCheckCircle className="enforcement-alert-icon" style={{ color: '#10b981' }} />
              <div className="enforcement-alert-content">
                <div className="enforcement-alert-title" style={{ color: '#065f46' }}>Operation Successful</div>
                <div className="enforcement-alert-desc" style={{ color: '#047857' }}>{apiSuccess}</div>
              </div>
              <button className="text-emerald-700 bg-transparent border-none cursor-pointer" onClick={() => setApiSuccess('')}><FaTimes /></button>
            </div>
          )}
        </div>

        {/* Global Organization Selector Context */}
        <div className="metering-card" style={{ padding: '18px 24px', marginBottom: 24, background: '#ffffff', border: '1px solid #e2e8f0', zIndex: 100, position: 'relative', overflow: 'visible' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ background: '#e0e7ff', color: '#4f46e5', width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaBuilding size={16} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Organization Scope</div>
                {loadingOrgs ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#64748b', marginTop: 2 }}>
                    <FaSpinner className="metering-spin" size={12} /> Loading organizations...
                  </div>
                ) : isOrgAdmin ? (
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', marginTop: 4 }}>
                    {organizations.find(o => o.id === selectedOrgId)?.name || 'No Organization Selected'}
                  </div>
                ) : (
                  <div style={{ marginTop: 4, position: 'relative', width: 320, zIndex: 100 }} ref={orgDropdownRef}>
                    <div
                      role="combobox"
                      aria-expanded={isOrgDropdownOpen}
                      aria-controls="org-dropdown-list"
                      tabIndex={0}
                      onClick={() => setIsOrgDropdownOpen(!isOrgDropdownOpen)}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', border: '1px solid #6366f1', borderRadius: '8px',
                        background: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: '#1e293b',
                        boxShadow: '0 0 0 1px rgba(99, 102, 241, 0.2)'
                      }}
                    >
                      <span>{organizations.find(o => o.id === selectedOrgId)?.name || 'Select Organization'}</span>
                      <FaChevronDown color="#94a3b8" size={12} />
                    </div>
                    {isOrgDropdownOpen && (
                      <div
                        id="org-dropdown-list"
                        style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '8px',
                        background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        zIndex: 200, display: 'flex', flexDirection: 'column'
                      }}>
                        <div style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 10px' }}>
                            <FaSearch color="#94a3b8" size={14} style={{ marginRight: '8px' }} />
                            <input
                              type="text"
                              placeholder="Search..."
                              value={orgSearchQuery}
                              onChange={(e) => setOrgSearchQuery(e.target.value)}
                              style={{ border: 'none', outline: 'none', width: '100%', fontSize: '13px', color: '#334155' }}
                            />
                          </div>
                        </div>
                        <div style={{ maxHeight: '250px', overflowY: 'auto', padding: '4px' }}>
                          {organizations
                            .filter(o =>
                              (o.name || '').toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
                              (o.reference || '').toLowerCase().includes(orgSearchQuery.toLowerCase())
                            )
                            .map(o => {
                              const isSelected = o.id === selectedOrgId;
                              return (
                                <div
                                  key={o.id}
                                  onClick={() => {
                                    setSelectedOrgId(o.id);
                                    try {
                                      localStorage.setItem('selected_organization_id', o.id);
                                    } catch (err) { }
                                    setEntitlements(null);
                                    setIsOrgDropdownOpen(false);
                                    setOrgSearchQuery('');
                                  }}
                                  style={{
                                    padding: '10px 12px', cursor: 'pointer', borderRadius: '6px',
                                    background: isSelected ? '#ede9fe' : 'transparent',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    marginBottom: '2px'
                                  }}
                                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                                >
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <span style={{ fontSize: '14px', fontWeight: 600, color: isSelected ? '#6366f1' : '#334155' }}>{o.name}</span>
                                    <span style={{ fontSize: '12px', color: isSelected ? '#818cf8' : '#94a3b8' }}>ID: {o.id || o.reference}</span>
                                  </div>
                                  {isSelected && <FaCheck color="#6366f1" size={14} />}
                                </div>
                              );
                            })}
                          {organizations.filter(o =>
                            (o.name || '').toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
                            (o.reference || '').toLowerCase().includes(orgSearchQuery.toLowerCase())
                          ).length === 0 && (
                              <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: '#94a3b8' }}>No organizations found</div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Current Assigned Tier Quick Info */}
            {entitlements && (() => {
              const remainingInfo = getRemainingDaysInfo(entitlements.effectiveToUtc, entitlements.assignedAtUtc, entitlements.effectiveFromUtc);
              const StatusIcon = remainingInfo?.icon || FaShieldAlt;
              const rawTierKey = (entitlements.tierKey || '').replace(/_/g, ' ').toUpperCase();

              return (
                <div className="license-header-card">
                  <div className="license-header-icon-box">
                    <FaShieldAlt size={18} />
                  </div>
                  <div className="license-header-content">
                    <div className="license-header-top">
                      <span className="license-header-label">ACTIVE LICENSE TIER</span>
                      {remainingInfo && (
                        <span className="license-status-pill" style={{
                          background: remainingInfo.theme.bg,
                          borderColor: remainingInfo.theme.border,
                          color: remainingInfo.theme.color
                        }}>
                          <span className="license-dot-pulse" style={{ background: remainingInfo.theme.dot }} />
                          <StatusIcon size={11} />
                          <span>{remainingInfo.label}</span>
                        </span>
                      )}
                    </div>
                    <div className="license-header-title-row">
                      <span className="license-header-name">{entitlements.tierName}</span>
                      <span className="license-key-badge">{rawTierKey}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Tab Selection */}
        <div className="metering-tabs">
          <button className={`metering-tab-btn ${activeTab === 'tiers' ? 'active' : ''}`} onClick={() => setActiveTab('tiers')}>
            <FaDatabase size={14} /> Licensing Tiers
          </button>
          <button className={`metering-tab-btn ${activeTab === 'assignments' ? 'active' : ''}`} onClick={() => setActiveTab('assignments')}>
            <FaBuilding size={14} /> Org Assignments
          </button>
          <button className={`metering-tab-btn ${activeTab === 'billing' ? 'active' : ''}`} onClick={() => setActiveTab('billing')}>
            <FaCalculator size={14} /> Metering & Billing
          </button>
          {isSystemAdmin && (
            <button className={`metering-tab-btn ${activeTab === 'reconciliation' ? 'active' : ''}`} onClick={() => setActiveTab('reconciliation')}>
              <FaSyncAlt size={14} /> Reconciliation
            </button>
          )}
          {isSystemAdmin && isDataGovernanceEnabled && (
            <button className={`metering-tab-btn ${activeTab === 'retention' ? 'active' : ''}`} onClick={() => setActiveTab('retention')}>
              <FaClock size={14} /> Retention & Holds
            </button>
          )}
          <button className={`metering-tab-btn ${activeTab === 'telemetry' ? 'active' : ''}`} onClick={() => setActiveTab('telemetry')}>
            <FaInfoCircle size={14} /> Telemetry
          </button>
        </div>


        {/* ==========================================
            2. Licensing Tiers Panel
            ========================================== */}
        {activeTab === 'tiers' && (
          <div className="metering-panel">
            <div className="metering-card">
              <div className="metering-card-header">
                <div className="metering-card-title"><FaDatabase /> Commercial Plans & Tiers</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    className="metering-btn metering-btn-secondary"
                    onClick={() => setShowPlansOverviewModal(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <FaThList size={13} /> Plans Overview
                  </button>
                  {isSystemAdmin && (
                    <button className="metering-btn metering-btn-primary" onClick={handleOpenNewTierModal}>
                      <FaPlus /> New Licensing Tier
                    </button>
                  )}
                </div>
              </div>

              {loadingTiers ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                  <FaSpinner className="metering-spin" size={24} /> Loading plans...
                </div>
              ) : (
                <div className="tiers-grid">
                  {displayTiers.map(t => {
                    const isCurrentTier = entitlements && entitlements.tierKey === t.tierKey;
                    return (
                      <div key={t.tierKey} className={`tier-card ${isCurrentTier ? 'current-tier' : ''}`}>
                        <div>
                          <div className="tier-card-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span className="tier-card-title">{t.name}</span>
                              {isCurrentTier && (
                                <span style={{ fontSize: 10, fontWeight: 700, background: '#e0e7ff', color: '#4f46e5', padding: '2px 6px', borderRadius: 4 }}>
                                  Current Plan
                                </span>
                              )}
                            </div>
                            {isSystemAdmin && <span className="tier-card-version">V{t.version}</span>}
                          </div>
                          <p className="tier-card-desc">{t.description}</p>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0' }}>
                            <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', width: '100%' }}>Enabled Features:</span>
                            {t.featurePolicies.filter(f => f.isEnabled).map(f => (
                              <span key={f.featureKey} style={{ background: '#f5f3ff', color: '#6366f1', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                                {capabilityMeta[f.featureKey]?.displayName || f.featureKey.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="tier-card-footer">
                          <span className={`status-badge ${t.status.toLowerCase()}`}>{t.status}</span>
                          {isSystemAdmin && (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="metering-btn metering-btn-secondary" style={{ padding: '4px 8px' }} onClick={() => handleOpenEditTier(t)}>
                                <FaEdit size={10} /> Edit
                              </button>
                              {t.status !== 'Active' && (
                                <button className="metering-btn metering-btn-danger" style={{ padding: '4px 8px' }} onClick={() => handleDeleteTier(t.tierKey)}>
                                  <FaTrash size={10} /> Delete
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Commercial Plans Overview & Comparison Modal */}
            {showPlansOverviewModal && (
              <div
                className="plans-overview-modal-overlay"
                onClick={() => setShowPlansOverviewModal(false)}
              >
                <div
                  className="plans-overview-modal-card"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="plans-overview-modal-header">
                    <div>
                      <div className="plans-overview-title">
                        <FaThList style={{ color: '#4f46e5' }} /> Commercial Plans & Capabilities Overview
                      </div>
                      <div className="plans-overview-subtitle">
                        Side-by-side comparison of features, quota allocations, limits, and overage rates across available commercial tiers.
                      </div>
                    </div>
                    <button
                      className="text-gray-400 bg-transparent border-none cursor-pointer"
                      style={{ padding: 4, cursor: 'pointer', background: 'none', border: 'none', color: '#94a3b8' }}
                      onClick={() => setShowPlansOverviewModal(false)}
                      title="Close overview (Esc)"
                    >
                      <FaTimes size={18} />
                    </button>
                  </div>

                  <div className="plans-overview-table-wrap hide-scrollbar">
                    <table className="plans-overview-table">
                      <thead>
                        <tr>
                          <th className="sticky-col" style={{ verticalAlign: 'bottom' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                              Tier / Plan Details
                            </span>
                          </th>
                          {displayTiers.map(t => {
                            const isCurrentTier = entitlements && entitlements.tierKey === t.tierKey;
                            return (
                              <th
                                key={t.tierKey}
                                className={isCurrentTier ? 'current-plan-col' : ''}
                                style={{ minWidth: 200, maxWidth: 260 }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 4 }}>
                                  <span style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>{t.name}</span>
                                  {isCurrentTier && (
                                    <span style={{ fontSize: 9, fontWeight: 700, background: '#e0e7ff', color: '#4f46e5', padding: '2px 6px', borderRadius: 4 }}>
                                      Current Plan
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                  <span className={`status-badge ${t.status.toLowerCase()}`} style={{ fontSize: 9, padding: '2px 6px' }}>
                                    {t.status}
                                  </span>
                                  {isSystemAdmin && (
                                    <span className="tier-card-version" style={{ fontSize: 9, padding: '1px 5px' }}>
                                      V{t.version}
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 400, lineHeight: 1.4 }}>
                                  {t.description || 'No description provided.'}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Section 1: Limits & Quotas */}
                        <tr className="plans-overview-section-header">
                          <th colSpan={displayTiers.length + 1}>
                            <FaCalculator size={11} style={{ marginRight: 6 }} /> Allocation Limits & Quotas
                          </th>
                        </tr>
                        {allOverviewLimitKeys.length === 0 ? (
                          <tr>
                            <td colSpan={displayTiers.length + 1} style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', padding: 16 }}>
                              No limit policies configured for these tiers.
                            </td>
                          </tr>
                        ) : (
                          allOverviewLimitKeys.map(lKey => {
                            const displayName = capabilityMeta[lKey]?.displayName || lKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            return (
                              <tr key={lKey}>
                                <td className="sticky-col">
                                  <div>{displayName}</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>{lKey}</div>
                                </td>
                                {displayTiers.map(t => {
                                  const isCurrentTier = entitlements && entitlements.tierKey === t.tierKey;
                                  const policy = (t.limitPolicies || []).find(p => p.limitKey === lKey);
                                  if (!policy) {
                                    return (
                                      <td key={t.tierKey} className={isCurrentTier ? 'current-plan-col' : ''} style={{ color: '#94a3b8', textAlign: 'center' }}>
                                        —
                                      </td>
                                    );
                                  }

                                  const isUnlimited = policy.value === -1 || (policy.value === 0 && policy.hardLimit === -1);
                                  const formattedValue = isUnlimited
                                    ? 'Unlimited'
                                    : `${(policy.value ?? 0).toLocaleString()} ${policy.unit || 'units'}`;

                                  return (
                                    <td key={t.tierKey} className={isCurrentTier ? 'current-plan-col' : ''}>
                                      <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 13 }}>
                                        {formattedValue}
                                      </div>
                                      <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {policy.hardLimit !== undefined && policy.hardLimit !== policy.value && (
                                          <span>Hard Cap: {policy.hardLimit === -1 ? 'Unlimited' : policy.hardLimit.toLocaleString()}</span>
                                        )}
                                        {policy.softLimitPercentage && (
                                          <span>Soft Warning: {policy.softLimitPercentage}%</span>
                                        )}
                                        {policy.overageRate > 0 ? (
                                          <span style={{ color: '#047857', fontWeight: 600 }}>
                                            +{policy.currencyCode || '$'}{policy.overageRate} / {policy.unit || 'unit'}
                                          </span>
                                        ) : (
                                          <span style={{ color: '#94a3b8' }}>No overage fee</span>
                                        )}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })
                        )}

                        {/* Section 2: Platform Features */}
                        <tr className="plans-overview-section-header">
                          <th colSpan={displayTiers.length + 1}>
                            <FaShieldAlt size={11} style={{ marginRight: 6 }} /> Platform Features & Capabilities
                          </th>
                        </tr>
                        {allOverviewFeatureKeys.length === 0 ? (
                          <tr>
                            <td colSpan={displayTiers.length + 1} style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', padding: 16 }}>
                              No feature policies configured for these tiers.
                            </td>
                          </tr>
                        ) : (
                          allOverviewFeatureKeys.map(fKey => {
                            const displayName = capabilityMeta[fKey]?.displayName || fKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            return (
                              <tr key={fKey}>
                                <td className="sticky-col">
                                  <div>{displayName}</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>{fKey}</div>
                                </td>
                                {displayTiers.map(t => {
                                  const isCurrentTier = entitlements && entitlements.tierKey === t.tierKey;
                                  const policy = (t.featurePolicies || []).find(p => p.featureKey === fKey);
                                  const isEnabled = Boolean(policy?.isEnabled);

                                  return (
                                    <td key={t.tierKey} className={isCurrentTier ? 'current-plan-col' : ''} style={{ textAlign: 'center' }}>
                                      {isEnabled ? (
                                        <span className="feature-included-pill">
                                          <FaCheck size={10} /> Included
                                        </span>
                                      ) : (
                                        <span className="feature-excluded-pill">
                                          —
                                        </span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      Showing {displayTiers.length} commercial plans & tiers.
                      {entitlements?.tierName && (
                        <span style={{ marginLeft: 6 }}>Active organization plan: <strong style={{ color: '#4f46e5' }}>{entitlements.tierName}</strong></span>
                      )}
                    </div>
                    <button
                      className="metering-btn metering-btn-secondary"
                      onClick={() => setShowPlansOverviewModal(false)}
                    >
                      Close Overview
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Licensing Tier Modal (Add / Edit) */}
            {showTierModal && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                <div className="metering-card" style={{ width: '100%', maxWidth: 750, maxHeight: '90vh', background: '#fff', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
                  <div className="metering-card-header" style={{ padding: '24px 24px 16px 24px', margin: 0, borderBottom: '1px solid #f1f5f9' }}>
                    <div className="metering-card-title">
                      {editingTierKey ? `Edit Tier: ${tierForm.name}` : 'Create Licensing Tier Plan'}
                    </div>
                    <button
                      className="text-gray-400 bg-transparent border-none cursor-pointer"
                      onClick={() => !savingTier && setShowTierModal(false)}
                      disabled={savingTier}
                    >
                      <FaTimes size={18} />
                    </button>
                  </div>
                  <form onSubmit={handleSaveTier} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <div className="hide-scrollbar" style={{ overflowY: 'auto', padding: '16px 24px', flex: 1 }}>
                      <div className="metering-form-grid">
                        <div className="metering-field">
                          <label className="metering-label">Tier Key (Unique URL ID)</label>
                          <input
                            type="text"
                            className="metering-input"
                            placeholder="e.g. enterprise_pro"
                            value={tierForm.tierKey}
                            onChange={(e) => setTierForm({ ...tierForm, tierKey: e.target.value })}
                            disabled={Boolean(editingTierKey) || savingTier}
                            required
                          />
                        </div>
                        <div className="metering-field">
                          <label className="metering-label">Display Name</label>
                          <input
                            type="text"
                            className="metering-input"
                            placeholder="e.g. Enterprise Pro"
                            value={tierForm.name}
                            onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })}
                            disabled={savingTier}
                            required
                          />
                        </div>
                      </div>

                      <div className="metering-field" style={{ marginBottom: 16 }}>
                        <label className="metering-label">Commercial Description</label>
                        <textarea
                          className="metering-textarea"
                          placeholder="Write plan descriptions visible to users..."
                          value={tierForm.description}
                          onChange={(e) => setTierForm({ ...tierForm, description: e.target.value })}
                          disabled={savingTier}
                          required
                        />
                      </div>

                      <div className="metering-form-grid" style={{ marginBottom: 16 }}>
                        <div className="metering-field">
                          <label className="metering-label">Tier Lifecycle Status</label>
                          <select
                            className="metering-select"
                            value={tierForm.status}
                            onChange={(e) => setTierForm({ ...tierForm, status: e.target.value })}
                            disabled={savingTier}
                          >
                            <option value="Draft">Draft</option>
                            <option value="Active">Active (Available for assignments)</option>
                            <option value="Deprecated">Deprecated (Only valid for current assignments)</option>
                            <option value="Retired">Retired (Triggers replacement migration)</option>
                          </select>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                            <input
                              type="checkbox"
                              id="isDefaultTier"
                              checked={tierForm.isDefaultTier}
                              onChange={(e) => setTierForm({ ...tierForm, isDefaultTier: e.target.checked })}
                              disabled={savingTier}
                            />
                            <label htmlFor="isDefaultTier" style={{ fontSize: 13, fontWeight: 700, color: '#334155', cursor: 'pointer', transition: 'color 0.2s ease' }} className="hover-color-primary">
                              Mark as Default Tier for New Organizations
                            </label>
                          </div>
                        </div>
                        <div></div>
                      </div>

                      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: '#4f46e5', marginBottom: 12 }}>
                          Feature Toggles (Policies)
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                          {tierForm.featurePolicies
                            .slice()
                            .sort((a, b) => {
                              const nameA = capabilityMeta[a.featureKey]?.displayName || a.featureKey;
                              const nameB = capabilityMeta[b.featureKey]?.displayName || b.featureKey;
                              return nameA.localeCompare(nameB);
                            })
                            .map(f => {
                              const meta = capabilityMeta[f.featureKey] || {};
                              return (
                                <div key={f.featureKey} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', boxSizing: 'border-box' }}>
                                  <input
                                    type="checkbox"
                                    id={`feature_${f.featureKey}`}
                                    checked={f.isEnabled}
                                    onChange={() => !savingTier && handleFeatureToggle(f.featureKey)}
                                    disabled={savingTier}
                                  />
                                  <label htmlFor={`feature_${f.featureKey}`} style={{ fontSize: 12, fontWeight: 700, color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {meta.displayName || f.featureKey.replace(/_/g, ' ')}
                                    {meta.lifecycle === 'Preview' && (
                                      <span style={{ fontSize: 9, background: '#fef9c3', color: '#a16207', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>PREVIEW</span>
                                    )}
                                  </label>
                                </div>
                              );
                            })}
                        </div>
                      </div>

                      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginBottom: 20 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: '#4f46e5', marginBottom: 12 }}>
                          Resource Limits & Billing policies
                        </div>
                        <div className="metering-table-container">
                          <table className="metering-table">
                            <thead>
                              <tr>
                                <th>Metric Target</th>
                                <th>Allowed Vol</th>
                                <th>Unit</th>
                                <th>Soft Warning %</th>
                                <th>Hard Block</th>
                                <th>Overage Fee</th>
                                <th>Curr</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tierForm.limitPolicies
                                .slice()
                                .sort((a, b) => {
                                  const nameA = capabilityMeta[a.limitKey]?.displayName || a.limitKey;
                                  const nameB = capabilityMeta[b.limitKey]?.displayName || b.limitKey;
                                  return nameA.localeCompare(nameB);
                                })
                                .map(l => (
                                  <tr key={l.limitKey}>
                                    <td style={{ fontWeight: 700, color: '#334155' }}>
                                      {capabilityMeta[l.limitKey]?.displayName || l.limitKey.replace(/_/g, ' ')}
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        min="0"
                                        className="metering-input"
                                        style={{ width: 85, padding: '4px 6px' }}
                                        value={l.value}
                                        onChange={(e) => handleLimitPolicyChange(l.limitKey, 'value', Number(e.target.value))}
                                        onWheel={(e) => e.target.blur()}
                                        disabled={savingTier}
                                      />
                                    </td>
                                    <td><span style={{ fontSize: 11, color: '#64748b' }}>{l.unit}</span></td>
                                    <td>
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        className="metering-input"
                                        style={{ width: 55, padding: '4px 6px' }}
                                        value={l.softLimitPercentage}
                                        onChange={(e) => handleLimitPolicyChange(l.limitKey, 'softLimitPercentage', Number(e.target.value))}
                                        onWheel={(e) => e.target.blur()}
                                        disabled={savingTier}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        min="0"
                                        className="metering-input"
                                        style={{ width: 85, padding: '4px 6px' }}
                                        value={l.hardLimit}
                                        onChange={(e) => handleLimitPolicyChange(l.limitKey, 'hardLimit', Number(e.target.value))}
                                        onWheel={(e) => e.target.blur()}
                                        disabled={savingTier}
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="metering-input"
                                        style={{ width: 65, padding: '4px 6px' }}
                                        value={l.overageRate}
                                        onChange={(e) => handleLimitPolicyChange(l.limitKey, 'overageRate', Number(e.target.value))}
                                        onWheel={(e) => e.target.blur()}
                                        disabled={savingTier}
                                      />
                                    </td>
                                    <td><span style={{ fontSize: 11, color: '#64748b' }}>{l.currencyCode}</span></td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: '0 0 20px 20px' }}>
                      <button
                        type="button"
                        className="metering-btn metering-btn-secondary"
                        onClick={() => setShowTierModal(false)}
                        disabled={savingTier}
                      >
                        Discard
                      </button>
                      <button
                        type="submit"
                        className="metering-btn metering-btn-primary"
                        disabled={savingTier}
                      >
                        {savingTier ? (
                          <>
                            <FaSpinner className="metering-spin" style={{ marginRight: 6 }} /> Saving...
                          </>
                        ) : (
                          <>
                            <FaCheck /> {editingTierKey ? 'Save Changes' : 'Create Plan'}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==========================================
            3. Org Assignments Panel
            ========================================== */}
        {activeTab === 'assignments' && (
          <div className="metering-panel entitlements-grid">
            <div className="metering-card modern-entitlements-card" style={{ alignSelf: 'flex-start' }}>
              <div className="metering-card-header">
                <div className="metering-card-title"><FaBuilding /> Entitlements Overview</div>
                {isSystemAdmin && <span className="tier-card-version">Version {entitlements?.version || 1}</span>}
              </div>

              {entitlements ? (
                <div>
                  {/* License Summary Banner */}
                  <div className="license-summary-banner">
                    <div className="banner-top-row">
                      <div>
                        <h3 className="banner-tier-title">{entitlements.tierName} Plan</h3>
                        <div className="banner-key-sub">
                          Entitlement Key: <code>{entitlements.tierKey}</code>
                        </div>
                      </div>
                      {(() => {
                        const remInfo = getRemainingDaysInfo(entitlements.effectiveToUtc, entitlements.assignedAtUtc, entitlements.effectiveFromUtc);
                        if (!remInfo) return null;
                        const IconComp = remInfo.icon;
                        return (
                          <div className="banner-remaining-badge" style={{
                            background: remInfo.theme.bg,
                            borderColor: remInfo.theme.border,
                            color: remInfo.theme.color
                          }}>
                            <IconComp size={14} />
                            <span>{remInfo.label}</span>
                          </div>
                        );
                      })()}
                    </div>

                    {/* License Validity Dates & Progress Bar */}
                    <div className="license-timeline-box">
                      <div className="timeline-dates-row">
                        <div className="timeline-date-item">
                          <span className="date-label">Assigned Date (Effective From)</span>
                          <span className="date-value">
                            {formatUtcDate(entitlements.effectiveFromUtc || entitlements.assignedAtUtc)}
                          </span>
                        </div>
                        <div className="timeline-date-item" style={{ textAlign: 'right' }}>
                          <span className="date-label">Effective To</span>
                          <span className="date-value">
                            {entitlements.effectiveToUtc ? formatUtcDate(entitlements.effectiveToUtc) : 'Ongoing / Lifetime'}
                          </span>
                        </div>
                      </div>

                      {entitlements.effectiveToUtc && (entitlements.effectiveFromUtc || entitlements.assignedAtUtc) && (() => {
                        const remInfo = getRemainingDaysInfo(entitlements.effectiveToUtc, entitlements.assignedAtUtc, entitlements.effectiveFromUtc);
                        if (!remInfo) return null;
                        return (
                          <div className="validity-progress-container">
                            <div className="validity-progress-track">
                              <div
                                className="validity-progress-fill"
                                style={{
                                  width: `${remInfo.percentConsumed}%`,
                                  background: remInfo.status === 'expired'
                                    ? '#ef4444'
                                    : remInfo.status === 'warning'
                                      ? 'linear-gradient(90deg, #f59e0b, #ea580c)'
                                      : 'linear-gradient(90deg, #6366f1, #10b981)'
                                }}
                              />
                            </div>
                            <div className="validity-progress-text">
                              <span>{remInfo.percentConsumed}% duration elapsed</span>
                              <span>{remInfo.days > 0 ? `${remInfo.days} days left` : remInfo.days === 0 ? 'Expires today' : 'Expired'}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Capability Features */}
                  <div style={{ marginBottom: 24 }}>
                    <div className="metering-label" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FaShieldAlt style={{ color: '#6366f1' }} /> Included Capability Features:
                    </div>
                    <div className="capability-features-grid">
                      {(!entitlements.enabledFeatures || entitlements.enabledFeatures.length === 0) ? (
                        <span style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>No features enabled in this tier.</span>
                      ) : (
                        (entitlements.enabledFeatures || []).map(f => (
                          <div key={f} className="capability-feature-chip">
                            <FaCheckCircle className="chip-icon" />
                            <span>{f.replace(/_/g, ' ')}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Usage Gauges */}
                  <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 20 }}>
                    <div className="metering-label" style={{ marginBottom: 14 }}>Resource Usage & Quotas:</div>
                    {usages.length === 0 ? (
                      <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>No usage metrics available.</div>
                    ) : (
                      usages.map(u => {
                        const USER_LIMIT_KEYS = ['max_users_per_org', 'business_active_users'];
                        const entLimit = (entitlements.limits || []).find(l =>
                          l.limitKey === u.limitKey ||
                          (USER_LIMIT_KEYS.includes(u.limitKey) && USER_LIMIT_KEYS.includes(l.limitKey))
                        ) || { value: 0, unit: 'units' };
                        const entLimitValue = entLimit.value !== undefined ? entLimit.value : (entLimit.allowed || 0);
                        const percent = entLimitValue > 0 ? Math.min(100, Math.floor((u.consumed / entLimitValue) * 100)) : 0;
                        const isHigh = percent >= 90;
                        const isMed = percent >= 80;

                        return (
                          <div key={u.limitKey} className="usage-meter-item">
                            <div className="meter-info-row">
                              <span className="meter-name">{u.limitKey.replace(/_/g, ' ')}</span>
                              <span className="meter-values">
                                <b>{getLimitValDisplay(u.limitKey, u.consumed)}</b> / {getLimitValDisplay(u.limitKey, entLimitValue)} {entLimit.unit}
                                <span className="meter-percent-chip" style={{
                                  background: isHigh ? '#fee2e2' : isMed ? '#fef3c7' : '#f1f5f9',
                                  color: isHigh ? '#b91c1c' : isMed ? '#b45309' : '#475569'
                                }}>
                                  {percent}%
                                </span>
                              </span>
                            </div>
                            <div className="meter-track">
                              <div
                                className="meter-fill"
                                style={{
                                  width: `${percent}%`,
                                  background: isHigh
                                    ? 'linear-gradient(90deg, #f87171, #ef4444)'
                                    : isMed
                                      ? 'linear-gradient(90deg, #fbbf24, #f59e0b)'
                                      : 'linear-gradient(90deg, #818cf8, #4f46e5)'
                                }}
                              />
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                  Select an organization above to view entitlements snapshot.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Assignment Form */}
              {isSystemAdmin && (
                <div className="metering-card">
                  <div className="metering-card-header">
                    <div className="metering-card-title"><FaPlus /> Change License Tier Assignment</div>
                  </div>
                  <form onSubmit={handleAssignTierSubmit}>
                    <div className="metering-field" style={{ marginBottom: 12 }}>
                      <label className="metering-label">Select Commercial Plan</label>
                      <select
                        className="metering-select"
                        value={assignmentForm.tierKey}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, tierKey: e.target.value })}
                        required
                      >
                        <option value="">-- Choose Plan --</option>
                        {(tiers || []).map(t => {
                          const isAssignable = t.status === 'Active';
                          return (
                            <option
                              key={t.tierKey}
                              value={t.tierKey}
                              disabled={!isAssignable}
                            >
                              {t.name} (V{t.version}){isAssignable ? '' : ` - [${t.status} - Not Assignable]`}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div className="metering-form-grid" style={{ marginBottom: 12 }}>
                      <div className="metering-field">
                        <label className="metering-label">Effective From Date</label>
                        <input
                          type="date"
                          className="metering-input"
                          min={new Date().toISOString().split('T')[0]}
                          value={assignmentForm.effectiveFromDate}
                          onChange={(e) => setAssignmentForm({ ...assignmentForm, effectiveFromDate: e.target.value })}
                          required
                        />
                      </div>
                      <div className="metering-field">
                        <label className="metering-label">Effective To Date</label>
                        <input
                          type="date"
                          className="metering-input"
                          min={assignmentForm.effectiveFromDate || new Date().toISOString().split('T')[0]}
                          value={assignmentForm.effectiveToDate}
                          onChange={(e) => setAssignmentForm({ ...assignmentForm, effectiveToDate: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="metering-form-grid" style={{ marginBottom: 12 }}>
                      <div className="metering-field">
                        <label className="metering-label">Reason Code</label>
                        <select
                          className="metering-select"
                          value={assignmentForm.reasonCode}
                          onChange={(e) => setAssignmentForm({ ...assignmentForm, reasonCode: e.target.value })}
                        >
                          <option value="upgrade">Upgrade</option>
                          <option value="downgrade">Downgrade</option>
                          <option value="migration">Migration (Tier Retirement)</option>
                          <option value="correction">Billing Correction</option>
                        </select>
                      </div>
                      <div className="metering-field">
                        <label className="metering-label">Operator Notes</label>
                        <input
                          type="text"
                          className="metering-input"
                          placeholder="Explain details for compliance audits..."
                          value={assignmentForm.notes}
                          onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                        />
                      </div>
                    </div>

                    <button type="submit" className="metering-btn metering-btn-primary" style={{ width: '100%' }} disabled={assigningTier || !assignmentForm.tierKey}>
                      {assigningTier ? <FaSpinner className="metering-spin" /> : <FaCheck />} Update Assignment Plan
                    </button>
                  </form>
                </div>
              )}

              {/* License Assignment History */}
              <div className="metering-card">
                <div className="metering-card-header">
                  <div className="metering-card-title"><FaHistory /> License Assignment History</div>
                  <span style={{ fontSize: 11, background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>
                    {assignmentHistory.length} Record{assignmentHistory.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {assignmentHistory.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 13, fontStyle: 'italic' }}>
                    No license assignments recorded for this organization.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {(() => {
                      const sortedHistory = [...assignmentHistory].map(item => {
                        const isCurrent = currentAssignment?.id != null
                          ? currentAssignment.id === item.id
                          : currentAssignment?.tierKey === item.tierKey && currentAssignment?.effectiveFromUtc === item.effectiveFromUtc;
                        return { ...item, _isCurrent: isCurrent };
                      }).sort((a, b) => {
                        if (a._isCurrent && !b._isCurrent) return -1;
                        if (!a._isCurrent && b._isCurrent) return 1;
                        return new Date(b.effectiveFromUtc || 0) - new Date(a.effectiveFromUtc || 0);
                      });

                      return sortedHistory.map((item, idx) => {
                        const matchedTier = (tiers || []).find(t => t.tierKey === item.tierKey);
                        const displayName = matchedTier ? matchedTier.name : item.tierKey;
                        const isCurrent = item._isCurrent;

                        return (
                          <div
                            key={item.id || idx}
                            style={{
                              padding: 12,
                              borderRadius: 10,
                              border: isCurrent ? '1.5px solid #6366f1' : '1px solid #e2e8f0',
                              background: isCurrent ? '#f5f3ff' : '#f8fafc'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <div style={{ fontWeight: 800, fontSize: 13, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span>{displayName}</span>
                                {isSystemAdmin && <span className="tier-card-version" style={{ fontSize: 10 }}>V{item.tierVersion || 1}</span>}
                              </div>
                              <span className={`status-badge ${isCurrent ? 'active' : 'retired'}`} style={{ fontSize: 10, padding: '2px 8px' }}>
                                {isCurrent ? 'CURRENT' : item.reasonCode ? item.reasonCode.toUpperCase() : 'PAST'}
                              </span>
                            </div>

                            <div style={{ fontSize: 11, color: '#64748b', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                              <div>
                                Assigned Date: <b style={{ color: '#334155' }}>{formatUtcDate(item.assignedAtUtc || item.effectiveFromUtc)}</b>
                              </div>
                              <div>
                                Reason: <b style={{ color: '#334155', textTransform: 'capitalize' }}>{item.reasonCode || '—'}</b>
                              </div>
                              <div>
                                Effective From: <b style={{ color: '#334155' }}>{formatUtcDate(item.effectiveFromUtc || item.assignedAtUtc)}</b>
                              </div>
                              <div>
                                Effective To: <b style={{ color: '#334155' }}>{item.effectiveToUtc ? formatUtcDate(item.effectiveToUtc) : 'Present'}</b>
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ==========================================
            4. Metering & Billing Panel
            ========================================== */}
        {activeTab === 'billing' && (
          <div className="metering-panel">
            <div className="metering-card">
              <div className="metering-card-header">
                <div className="metering-card-title"><FaCalculator /> Active Billing Cycle Summary</div>
              </div>

              {loadingBilling ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                  <FaSpinner className="metering-spin" /> Calculating breakdown segments...
                </div>
              ) : cycleBreakdown ? (() => {
                const matchedSummaryTier = tiers.find(t => t.tierKey === billingSummary?.tierKey);
                const summaryLicenseName = matchedSummaryTier ? matchedSummaryTier.name : (
                  billingSummary?.tierKey
                    ? billingSummary.tierKey.charAt(0).toUpperCase() + billingSummary.tierKey.slice(1)
                    : 'No Plan Assigned'
                );

                const formatDate = (dateStr) => {
                  if (!dateStr) return 'N/A';
                  try {
                    const d = new Date(dateStr);
                    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString();
                  } catch (e) {
                    return 'N/A';
                  }
                };

                return (
                  <div>
                    {/* Billing Cycle Details Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '16px',
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      marginBottom: '20px'
                    }}>
                      <div>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, display: 'block', marginBottom: '2px' }}>License Plan</span>
                        <span style={{ fontSize: '14px', fontWeight: 800, color: '#1e293b' }}>{summaryLicenseName}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, display: 'block', marginBottom: '2px' }}>Billing Cycle Start</span>
                        <span style={{ fontSize: '14px', fontWeight: 800, color: '#1e293b' }}>{formatDate(billingSummary?.billingCycleStartUtc)}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, display: 'block', marginBottom: '2px' }}>Billing Cycle End</span>
                        <span style={{ fontSize: '14px', fontWeight: 800, color: '#1e293b' }}>{formatDate(billingSummary?.billingCycleEndUtc)}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, display: 'block', marginBottom: '2px' }}>Billing Cycle Anchor</span>
                        <span style={{ fontSize: '14px', fontWeight: 800, color: '#1e293b' }}>{formatDate(cycleBreakdown?.billingCycleAnchorUtc)}</span>
                      </div>
                    </div>
                  </div>
                );
              })() : (
                <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>
                  No billing breakdown data available.
                </div>
              )}
            </div>

            {/* Bottom Full-Width Section: Module Ingestion & Usage Telemetry Breakdown */}
            <div className="module-telemetry-section">
              <div className="module-telemetry-header">
                <div className="module-telemetry-title">
                  <FaLayerGroup style={{ color: '#4f46e5' }} /> Module Usage Telemetry Ingestion Details
                  {loadingModuleUsages && <FaSpinner className="spin" size={14} style={{ marginLeft: 8, color: '#6366f1' }} />}
                </div>
              </div>

              {/* Module Telemetry Cards Grid */}
              <div className="module-cards-grid">
                {(() => {
                  const standardModulesList = ['content_studio', 'player', 'extension_player'];
                  const returnedModules = (Array.isArray(moduleUsages) ? moduleUsages : [])
                    .filter(mod => mod.moduleKey !== 'iap_admin');

                  // Map to merge API response into standard modules
                  const moduleMap = new Map();
                  standardModulesList.forEach(key => {
                    moduleMap.set(key, {
                      moduleKey: key,
                      windowStartUtc: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
                      windowEndUtc: new Date().toISOString(),
                      dimensions: []
                    });
                  });

                  returnedModules.forEach(mod => {
                    const normKey = (mod.moduleKey === 'sdk') ? 'player' : mod.moduleKey;
                    const existing = moduleMap.get(normKey) || {};
                    moduleMap.set(normKey, { ...existing, ...mod, moduleKey: normKey });
                  });

                  const mergedModules = Array.from(moduleMap.values());

                  if (mergedModules.length === 0) {
                    return (
                      <div style={{ gridColumn: '1 / -1', background: '#ffffff', padding: '32px', borderRadius: '12px', textAlign: 'center', border: '1px dashed #cbd5e1', color: '#64748b' }}>
                        {loadingModuleUsages ? (
                          <span><FaSpinner className="spin" /> Loading module usage telemetry...</span>
                        ) : (
                          <span>No module usage telemetry records available.</span>
                        )}
                      </div>
                    );
                  }

                  return mergedModules.map(mod => {
                    const meta = MODULE_METADATA[mod.moduleKey] || {
                      title: mod.moduleKey,
                      workflow: `${mod.moduleKey} Workflows`,
                      description: `Telemetry ingestion for ${mod.moduleKey}`,
                      icon: FaCube,
                      color: '#4f46e5',
                      bg: '#e0e7ff'
                    };

                    const IconComp = meta.icon;

                    return (
                      <div key={mod.moduleKey} className="module-telemetry-card">
                        {/* Top Card Info */}
                        <div className="module-card-top">
                          <div className="module-card-heading">
                            <div className="module-card-icon" style={{ background: meta.bg, color: meta.color }}>
                              <IconComp />
                            </div>
                            <div>
                              <div className="module-card-title-text">{meta.title}</div>
                              <div className="module-card-desc">{meta.description}</div>
                            </div>
                          </div>
                          <span className="module-key-tag">{mod.moduleKey}</span>
                        </div>

                        {/* Window Range */}
                        <div className="module-window-box">
                          <div>
                            <span style={{ fontWeight: 700, color: '#475569' }}>Window Start: </span>
                            <span>{formatUtcDate(mod.windowStartUtc)}</span>
                          </div>
                          <div>
                            <span style={{ fontWeight: 700, color: '#475569' }}>Window End: </span>
                            <span>{formatUtcDate(mod.windowEndUtc)}</span>
                          </div>
                        </div>

                        {/* Dynamic Dimensions Grid */}
                        <div className="module-dimensions-grid">
                          {mod.dimensions && mod.dimensions.length > 0 ? (
                            mod.dimensions.map((dim, idx) => {
                              const rawName = dim.dimension || dim.name || 'EventsIngested';
                              const isStorage = rawName.toLowerCase().includes('storage') || dim.unit === 'bytes';

                              const formattedTitle = rawName
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, str => str.toUpperCase())
                                .trim();

                              const MetricIcon = isStorage ? FaHdd : FaChartBar;
                              const formattedValue = isStorage
                                ? formatByteSize(dim.quantity || 0)
                                : (dim.quantity ?? 0).toLocaleString();

                              return (
                                <div key={idx} className="dimension-metric-box">
                                  <div className="dimension-name" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <MetricIcon size={11} style={{ color: meta.color }} /> {formattedTitle}
                                  </div>
                                  <div className="dimension-value">
                                    {formattedValue}
                                  </div>
                                  <div className="dimension-meta">
                                    <span>Unit: {dim.unit || 'count'}</span>
                                    {dim.confidence && (
                                      <span className={`status-badge ${dim.confidence === 'RawFallback' ? 'draft' : 'active'}`} style={{ fontSize: '9px', padding: '1px 6px' }}>
                                        {dim.confidence}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <>
                              <div className="dimension-metric-box">
                                <div className="dimension-name" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <FaChartBar size={11} style={{ color: meta.color }} /> Events Ingested
                                </div>
                                <div className="dimension-value">0</div>
                                <div className="dimension-meta">
                                  <span>Unit: count</span>
                                </div>
                              </div>
                              <div className="dimension-metric-box">
                                <div className="dimension-name" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <FaHdd size={11} style={{ color: meta.color }} /> Storage Ingested
                                </div>
                                <div className="dimension-value">0 Bytes</div>
                                <div className="dimension-meta">
                                  <span>Raw: 0 B</span>
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Footer Status */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 6, borderTop: '1px solid #f1f5f9', fontSize: '11px', color: '#64748b' }}>
                          <span style={{ fontWeight: 600, color: meta.color }}>
                            {meta.workflow}
                          </span>
                          <span className="status-badge active" style={{ fontSize: '9px', padding: '2px 8px' }}>
                            Ingestion Active
                          </span>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Daily usage table */}
            <div className="metering-card">
              <div className="metering-card-header">
                <div className="metering-card-title"><FaAdjust /> Daily Usage Ingestion Trends</div>
              </div>
              {(Array.isArray(dailyUsageData) && dailyUsageData.length > 0) ? (
                <div className="metering-table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="metering-table">
                    <thead>
                      <tr>
                        <th style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>Date</th>
                        {getTableColumns().map(col => (
                          <th key={col} style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                            {formatHeader(col)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dailyUsageData.map((day, idx) => (
                        <tr key={day.date || idx}>
                          <td style={{ fontWeight: 700 }}>{day.date}</td>
                          {getTableColumns().map(col => (
                            <td key={col}>
                              {formatCellValue(col, day[col])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No trend data loaded.</div>
              )}
            </div>

            <div className="topology-grid" style={!isSystemAdmin ? { gridTemplateColumns: '1fr' } : {}}>
              {/* Metering Records List */}
              <div className="metering-card">
                <div className="metering-card-header">
                  <div className="metering-card-title"><FaDatabase /> Billing-ready Metering Records</div>
                </div>
                <div className="metering-table-container">
                  <table className="metering-table">
                    <thead>
                      <tr>
                        <th>Billing Dimension</th>
                        <th>Evidence Volume</th>
                        <th>Calculated Confidence</th>
                        <th>Finalized</th>
                        {isSystemAdmin && <th>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(!meteringRecords || meteringRecords.length === 0) ? (
                        <tr><td colSpan={isSystemAdmin ? "5" : "4"} style={{ textAlign: 'center', padding: 20 }}>No records found.</td></tr>
                      ) : (
                        (meteringRecords || []).map((rec, idx) => (
                          <tr key={rec.id || `${rec.dimension}-${idx}`}>
                            <td style={{ fontWeight: 700 }}>{rec.dimension.replace(/([A-Z])/g, ' $1')}</td>
                            <td>{getLimitValDisplay(rec.dimension.toLowerCase(), rec.quantity)}</td>
                            <td>
                              <span style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: rec.confidence === 'AggregateBacked' ? '#047857' : rec.confidence === 'RawFallback' ? '#b45309' : '#b91c1c',
                                background: rec.confidence === 'AggregateBacked' ? '#dcfce7' : rec.confidence === 'RawFallback' ? '#fef3c7' : '#fee2e2',
                                padding: '2px 6px',
                                borderRadius: 4
                              }}>
                                {rec.confidence.replace(/([A-Z])/g, ' $1')}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${rec.isFinalized ? 'active' : 'deprecated'}`}>
                                {rec.isFinalized ? 'Finalized' : 'Draft'}
                              </span>
                            </td>
                            {isSystemAdmin && (
                              <td>
                                <button
                                  className="metering-btn metering-btn-secondary"
                                  style={{ padding: '4px 8px', fontSize: 11 }}
                                  onClick={() => handleAdjustClick(rec)}
                                  disabled={rec.isFinalized}
                                >
                                  <FaAdjust /> Adjust
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Operations Panel */}
              {isSystemAdmin && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {/* Adjustments Panel */}
                  {adjustingRecord && (
                    <div className="metering-card" style={{ border: '2px solid #f59e0b', animation: 'metering-fadeIn 0.3s ease-out' }}>
                      <div className="metering-card-header">
                        <div className="metering-card-title" style={{ color: '#b45309' }}><FaAdjust /> Adjust Dimension</div>
                        <button className="text-gray-400 bg-transparent border-none cursor-pointer" onClick={() => setAdjustingRecord(null)}>
                          <FaTimes />
                        </button>
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                        Target: <b>{adjustingRecord.dimension.replace(/([A-Z])/g, ' $1')}</b>. Current quantity: <b>{adjustingRecord.quantity}</b>
                      </div>
                      <form onSubmit={handleSaveAdjustment}>
                        <div className="metering-field" style={{ marginBottom: 12 }}>
                          <label className="metering-label">Quantity Delta (Additive correction)</label>
                          <input
                            type="number"
                            className="metering-input"
                            placeholder="e.g. -150 or 500"
                            value={adjustmentForm.quantityDelta}
                            onChange={(e) => setAdjustmentForm({ ...adjustmentForm, quantityDelta: e.target.value })}
                            required
                          />
                        </div>
                        <div className="metering-field" style={{ marginBottom: 12 }}>
                          <label className="metering-label">Reason Code</label>
                          <select
                            className="metering-select"
                            value={adjustmentForm.reasonCode}
                            onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reasonCode: e.target.value })}
                          >
                            <option value="vendor-reconciliation">Vendor Reconciliation</option>
                            <option value="telemetry-outage">Telemetry Outage</option>
                            <option value="audit-correction">Compliance Audit Correction</option>
                          </select>
                        </div>
                        <div className="metering-field" style={{ marginBottom: 16 }}>
                          <label className="metering-label">Operator Comments</label>
                          <textarea
                            className="metering-textarea"
                            placeholder="Provide explanation for auditing..."
                            value={adjustmentForm.notes}
                            onChange={(e) => setAdjustmentForm({ ...adjustmentForm, notes: e.target.value })}
                            required
                          />
                        </div>
                        <button type="submit" className="metering-btn metering-btn-success" style={{ width: '100%' }}>
                          Apply Correction
                        </button>
                      </form>
                    </div>
                  )}

                  {/* Finalize Cycle Panel */}
                  <div className="metering-card">
                    <div className="metering-card-header">
                      <div className="metering-card-title"><FaLock /> Finalize Billing Cycle</div>
                    </div>
                    <div className="metering-form-grid" style={{ gridTemplateColumns: '1fr', gap: 12 }}>
                      <div className="metering-field">
                        <label className="metering-label">Cycle Start Date (UTC)</label>
                        <input
                          type="date"
                          className="metering-input"
                          value={finalizationDates.startUtc}
                          onChange={(e) => setFinalizationDates({ ...finalizationDates, startUtc: e.target.value })}
                        />
                      </div>
                      <div className="metering-field">
                        <label className="metering-label">Cycle End Date (UTC)</label>
                        <input
                          type="date"
                          className="metering-input"
                          value={finalizationDates.endUtc}
                          onChange={(e) => setFinalizationDates({ ...finalizationDates, endUtc: e.target.value })}
                        />
                      </div>
                    </div>
                    <button
                      className="metering-btn metering-btn-primary"
                      style={{ width: '100%', marginTop: 16 }}
                      onClick={handleFinalizeCycle}
                      disabled={finalizingBilling}
                    >
                      {finalizingBilling ? <FaSpinner className="metering-spin" /> : <FaLock />} Finalize & lock Records
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==========================================
            6. Reconciliation Panel (System Administrator Only)
            ========================================== */}
        {isSystemAdmin && activeTab === 'reconciliation' && (
          <div className="metering-panel" style={{ display: 'flex', flexDirection: 'column', gap: 24, animation: 'metering-fadeIn 0.3s ease-out' }}>
            {/* Card 1: Org Metering Reconciliation Status (Org Admin & System Admin) */}
            <div className="metering-card">
              <div className="metering-card-header">
                <div className="metering-card-title">
                  <FaSyncAlt /> Organization Metering Reconciliation Status
                </div>
                <button
                  className="metering-btn metering-btn-secondary"
                  onClick={() => loadTabContent()}
                  disabled={loadingReconciliation}
                >
                  <FaSyncAlt className={loadingReconciliation ? "metering-spin" : ""} /> Refresh Status
                </button>
              </div>

              {loadingReconciliation ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                  <FaSpinner className="metering-spin" size={24} /> Loading organization reconciliation status...
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active Scope</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                        {organizations.find(o => String(o.id) === String(selectedOrgId))?.name || 'Active Organization Scope'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Reconciliation Status</div>
                        {(() => {
                          const st = reconciliationData?.status || 'Reconciled';
                          let badgeStyle = { background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#047857' };
                          let label = 'Reconciled (In Parity)';
                          let icon = <FaCheckCircle style={{ marginRight: 4 }} />;

                          if (st === 'DriftDetected') {
                            badgeStyle = { background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c' };
                            label = 'Drift Detected';
                            icon = <FaExclamationTriangle style={{ marginRight: 4 }} />;
                          } else if (st === 'PendingReconciliation') {
                            badgeStyle = { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' };
                            label = 'Pending Reconciliation';
                            icon = <FaClock style={{ marginRight: 4 }} />;
                          } else if (st.includes('Manual') || st.includes('Hold')) {
                            badgeStyle = { background: '#fff7ed', border: '1px solid #fdba74', color: '#c2410c' };
                            label = 'Manual Review Required';
                            icon = <FaLock style={{ marginRight: 4 }} />;
                          }

                          return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700, ...badgeStyle }}>
                              {icon} {label}
                            </span>
                          );
                        })()}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 2 }}>Billing Confidence</div>
                        {(() => {
                          const conf = reconciliationData?.confidence || 'AggregateBacked';
                          let badgeStyle = { background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#047857' };
                          let label = 'Aggregate-Backed (High)';
                          let icon = <FaCheckCircle style={{ marginRight: 4 }} />;

                          if (conf === 'RawFallback') {
                            badgeStyle = { background: '#fffbe6', border: '1px solid #ffe58f', color: '#d46b08' };
                            label = 'Raw Fallback (Stale Aggregates)';
                            icon = <FaExclamationTriangle style={{ marginRight: 4 }} />;
                          } else if (conf === 'ThrottleOnlyCheckpoint') {
                            badgeStyle = { background: '#fff7ed', border: '1px solid #fdba74', color: '#c2410c' };
                            label = 'Checkpoint Only (Budget Exceeded)';
                            icon = <FaExclamationTriangle style={{ marginRight: 4 }} />;
                          } else if (conf === 'Incomplete') {
                            badgeStyle = { background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569' };
                            label = 'Incomplete Period Hold';
                            icon = <FaClock style={{ marginRight: 4 }} />;
                          }

                          return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 700, ...badgeStyle }}>
                              {icon} {label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Summary Callout & Details */}
                  <div style={{ background: reconciliationData?.status === 'DriftDetected' ? '#fff1f2' : '#f8fafc', border: `1px solid ${reconciliationData?.status === 'DriftDetected' ? '#fecdd3' : '#e2e8f0'}`, borderRadius: 10, padding: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      {reconciliationData?.status === 'DriftDetected' ? (
                        <FaExclamationTriangle size={18} style={{ color: '#e11d48', marginTop: 2 }} />
                      ) : (
                        <FaInfoCircle size={18} style={{ color: '#4f46e5', marginTop: 2 }} />
                      )}
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: reconciliationData?.status === 'DriftDetected' ? '#9f1239' : '#1e293b' }}>
                          {reconciliationData?.driftDetails || "No telemetry or aggregate drift detected for this organization."}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                          Last evaluated: <b>{formatDateTime(reconciliationData?.lastCheckedUtc)}</b>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Card 2: System Admin Policy Reconciliation Workflow */}
            {isSystemAdmin && (
              <div className="metering-card">
                <div className="metering-card-header">
                  <div className="metering-card-title">
                    <FaShieldAlt /> Policy Administration & System Reconciliation Run
                  </div>
                </div>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px 0' }}>
                  Execute system-wide policy reconciliation to calculate drift across raw telemetry, pre-computed aggregates, and live projections across all organization scopes.
                </p>
                <div>
                  <button
                    className="metering-btn metering-btn-primary"
                    onClick={() => setShowRunReconcileConfirm(true)}
                    disabled={runningReconciliation}
                  >
                    {runningReconciliation ? <FaSpinner className="metering-spin" /> : <FaSyncAlt />} Trigger System Policy Reconciliation
                  </button>
                </div>
              </div>
            )}

            {/* Card 3: Cross-Tenant Redacted Operational Summaries (System Admin Only) */}
            {isSystemAdmin && (
              <div className="metering-card">
                <div className="metering-card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
                  <div className="metering-card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaDatabase style={{ color: '#4f46e5' }} /> Cross-Tenant Redacted Operational Summaries
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, background: '#e0e7ff', border: '1px solid #c7d2fe', padding: '4px 10px', borderRadius: 6, color: '#3730a3', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <FaShieldAlt size={11} /> Minimum Threshold Active (≥ {crossTenantSummaries?.minimumAggregationThreshold ?? 3})
                    </span>
                    <span style={{ fontSize: 11, background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: 6, color: '#475569', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <FaLock size={10} /> System-Admin View Only
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
                  Privacy-safe operational view with minimum threshold suppression. Summaries present platform health without exposing unredacted customer usage.
                </div>

                {/* Date Window Filter Bar */}
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155' }}>
                      <FaClock style={{ color: '#6366f1' }} /> Evaluation Window:
                    </div>
                    <div style={{ fontSize: 12, background: '#ffffff', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: 6, color: '#0f172a', fontWeight: 600 }}>
                      {crossTenantSummaries?.windowStartUtc ? formatDateTime(crossTenantSummaries.windowStartUtc) : '—'} &nbsp;→&nbsp; {crossTenantSummaries?.windowEndUtc ? formatDateTime(crossTenantSummaries.windowEndUtc) : '—'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>From:</label>
                      <input
                        type="datetime-local"
                        className="metering-input"
                        style={{ padding: '4px 8px', fontSize: 11, width: 170 }}
                        value={summaryFromUtc}
                        onChange={(e) => setSummaryFromUtc(e.target.value)}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>To:</label>
                      <input
                        type="datetime-local"
                        className="metering-input"
                        style={{ padding: '4px 8px', fontSize: 11, width: 170 }}
                        value={summaryToUtc}
                        onChange={(e) => setSummaryToUtc(e.target.value)}
                      />
                    </div>
                    <button
                      className="metering-btn metering-btn-secondary"
                      style={{ padding: '4px 12px', fontSize: 11 }}
                      onClick={() => {
                        const from = summaryFromUtc ? new Date(summaryFromUtc).toISOString() : undefined;
                        const to = summaryToUtc ? new Date(summaryToUtc).toISOString() : undefined;
                        handleFetchCrossTenantSummaries(from, to);
                      }}
                      disabled={loadingCrossTenantSummaries}
                    >
                      {loadingCrossTenantSummaries ? <FaSpinner className="metering-spin" /> : <FaSyncAlt />} Apply Filter
                    </button>
                  </div>
                </div>

                {loadingCrossTenantSummaries ? (
                  <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>
                    <FaSpinner className="metering-spin" size={24} style={{ color: '#4f46e5' }} />
                    <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600 }}>Loading cross-tenant operational summaries...</div>
                  </div>
                ) : (
                  <>
                    {/* KPI Strip */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
                      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, borderLeft: '4px solid #4f46e5' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Qualified Organizations</div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
                          {crossTenantSummaries?.qualifiedOrganizationCount ?? 0}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Organizations meeting minimum threshold</div>
                      </div>

                      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, borderLeft: '4px solid #0284c7' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Suppressed Organizations</div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: '#0284c7', marginTop: 4 }}>
                          {crossTenantSummaries?.suppressedOrganizationCount ?? 0}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Suppressed to protect tenant anonymity</div>
                      </div>

                      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, borderLeft: '4px solid #059669' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aggregation Threshold</div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: '#059669', marginTop: 4 }}>
                          ≥ {crossTenantSummaries?.minimumAggregationThreshold ?? 3} Orgs
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Required count for unredacted rollup</div>
                      </div>
                    </div>

                    {/* Operational Usage Dimensions Section */}
                    {crossTenantSummaries?.dimensions && crossTenantSummaries.dimensions.length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaLayerGroup style={{ color: '#4f46e5' }} /> Operational Usage Dimensions
                        </div>
                        <div className="metering-table-wrapper">
                          <table className="metering-table">
                            <thead>
                              <tr>
                                <th>Dimension</th>
                                <th>Aggregated Quantity</th>
                                <th>Unit</th>
                                <th>Confidence</th>
                                <th>Privacy Protection</th>
                              </tr>
                            </thead>
                            <tbody>
                              {crossTenantSummaries.dimensions.map((dim, idx) => {
                                const conf = dim.confidence || 'AggregateBacked';
                                let badgeStyle = { background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#047857' };
                                let label = 'Aggregate-Backed';
                                if (conf === 'RawFallback') {
                                  badgeStyle = { background: '#fffbe6', border: '1px solid #ffe58f', color: '#d46b08' };
                                  label = 'Raw Fallback';
                                } else if (conf === 'Incomplete') {
                                  badgeStyle = { background: '#fef3c7', border: '1px solid #fde68a', color: '#b45309' };
                                  label = 'Incomplete';
                                }
                                return (
                                  <tr key={idx}>
                                    <td>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4f46e5' }} />
                                        <b style={{ color: '#0f172a', fontSize: 13 }}>{dim.dimension}</b>
                                      </div>
                                    </td>
                                    <td>
                                      <b style={{ fontSize: 15, color: '#0f172a' }}>{(dim.quantity || 0).toLocaleString()}</b>
                                    </td>
                                    <td>
                                      <span style={{ fontSize: 12, color: '#64748b', textTransform: 'lowercase', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                                        {dim.unit}
                                      </span>
                                    </td>
                                    <td>
                                      <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, ...badgeStyle }}>
                                        {dim.confidence || label}
                                      </span>
                                    </td>
                                    <td>
                                      <span style={{ fontSize: 11, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '3px 8px', borderRadius: 6, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        <FaShieldAlt size={10} /> Redacted & Aggregated
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Legacy Tenant Scope Table (fallback if summaries array exists) */}
                    {crossTenantSummaries?.summaries && crossTenantSummaries.summaries.length > 0 && (
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaBuilding style={{ color: '#0284c7' }} /> Tenant Scope Breakdown
                        </div>
                        <div className="metering-table-wrapper">
                          <table className="metering-table">
                            <thead>
                              <tr>
                                <th>Tenant Scope Alias</th>
                                <th>Billing Confidence</th>
                                <th>Reconciliation Status</th>
                                <th>Aggregated Volume</th>
                                <th>Privacy Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {crossTenantSummaries.summaries.map((summary, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <b style={{ color: '#0f172a' }}>{summary.tenantAlias}</b>
                                  </td>
                                  <td>
                                    {(() => {
                                      const conf = summary.billingConfidence || 'AggregateBacked';
                                      let badgeStyle = { background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#047857' };
                                      let label = 'Aggregate-Backed';
                                      if (conf === 'RawFallback') {
                                        badgeStyle = { background: '#fffbe6', border: '1px solid #ffe58f', color: '#d46b08' };
                                        label = 'Raw Fallback';
                                      } else if (conf === 'Incomplete') {
                                        badgeStyle = { background: '#fef3c7', border: '1px solid #fde68a', color: '#b45309' };
                                        label = 'Incomplete';
                                      }
                                      return (
                                        <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, ...badgeStyle }}>
                                          {label}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td>
                                    {(() => {
                                      const st = summary.status || 'Reconciled';
                                      let badgeStyle = { background: '#ecfdf5', border: '1px solid #6ee7b7', color: '#047857' };
                                      if (st === 'DriftDetected') {
                                        badgeStyle = { background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c' };
                                      }
                                      return (
                                        <span style={{ padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, ...badgeStyle }}>
                                          {st}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td>{(summary.aggregatedQuantityTotal || 0).toLocaleString()} events</td>
                                  <td>
                                    <span style={{ fontSize: 11, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '3px 8px', borderRadius: 6, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                      <FaShieldAlt size={10} /> Redacted / Protected
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Confirmation Modal for System Admin Policy Reconciliation Run */}
        {showRunReconcileConfirm && isSystemAdmin && (
          <div className="metering-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div className="metering-modal" style={{ background: '#ffffff', borderRadius: 14, width: '100%', maxWidth: 480, padding: 24, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaExclamationTriangle style={{ color: '#f59e0b' }} /> Confirm System Policy Reconciliation
                </div>
                <button className="text-gray-400 bg-transparent border-none cursor-pointer" onClick={() => setShowRunReconcileConfirm(false)}>
                  <FaTimes />
                </button>
              </div>
              <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, marginBottom: 20 }}>
                Are you sure you want to execute system-wide telemetry, aggregate, and projection policy reconciliation? This operation will calculate drift across all organization scopes and update reconciliation statuses.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button className="metering-btn metering-btn-secondary" onClick={() => setShowRunReconcileConfirm(false)}>
                  Cancel
                </button>
                <button className="metering-btn metering-btn-primary" onClick={handleTriggerReconciliation}>
                  Confirm & Run Reconciliation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ==========================================
            5. Retention & Holds Panel
            ========================================== */}
        {activeTab === 'retention' && isSystemAdmin && isDataGovernanceEnabled && (
          <div className="metering-panel topology-grid">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
              {/* Retention policies list */}
              <div className="metering-card" style={{ padding: '12px 16px' }}>
                <div className="metering-card-header" style={{ marginBottom: 12 }}>
                  <div className="metering-card-title"><FaClock /> Data Retention Policies</div>
                </div>
                {!isSystemAdmin ? (
                  <div style={{ padding: '24px 20px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                    <FaInfoCircle size={16} style={{ marginRight: 8, verticalAlign: 'middle', color: '#4f46e5' }} />
                    Data retention policies are managed globally by System Administrators.
                  </div>
                ) : (
                  retentionPolicies.map((p, index) => {
                    const getVal = (field) => {
                      if (editedPolicies[p.policyKey] && editedPolicies[p.policyKey][field] !== undefined) {
                        return editedPolicies[p.policyKey][field];
                      }
                      return p[field];
                    };
                    const isDryRun = getVal('dryRun');
                    const retentionDays = getVal('retentionDays');
                    const maxDeleteBatchSize = getVal('maxDeleteBatchSize');

                    const hasEdits = editedPolicies[p.policyKey] !== undefined;

                    return (
                      <div key={`${p.policyKey}-${index}`} className="policy-config-row" style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <div className="policy-config-left">
                            <span className="policy-config-name" style={{ fontWeight: 700, color: '#0f172a', fontSize: '13px' }}>{p.name}</span>
                            <div className="policy-config-meta" style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                              Key: <code>{p.policyKey}</code> | Resource: <b>{p.resourceType}</b> | Current Target: <b>{p.retentionDays} Days</b>{p.maxDeleteBatchSize ? ` | Max Batch: ${p.maxDeleteBatchSize}` : ''}{p.dryRun ? ' | [DryRun]' : ''}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, width: '100%', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            {/* DryRun */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="checkbox"
                                id={`dry_${p.policyKey}_${index}`}
                                checked={Boolean(isDryRun)}
                                onChange={(e) => {
                                  setEditedPolicies({
                                    ...editedPolicies,
                                    [p.policyKey]: {
                                      ...editedPolicies[p.policyKey],
                                      dryRun: e.target.checked
                                    }
                                  });
                                }}
                              />
                              <label htmlFor={`dry_${p.policyKey}_${index}`} style={{ fontSize: 11, fontWeight: 700, color: '#64748b', cursor: 'pointer' }}>Dry Run Mode</label>
                            </div>

                            {/* Retention Days */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <label style={{ fontSize: 10, fontWeight: 700, color: '#475569' }}>Retention Window (Days)</label>
                              <input
                                type="number"
                                className="metering-input"
                                style={{ width: 80, padding: '3px 6px', height: '28px', fontSize: '12px', borderRadius: '6px' }}
                                value={retentionDays}
                                onChange={(e) => {
                                  setEditedPolicies({
                                    ...editedPolicies,
                                    [p.policyKey]: {
                                      ...editedPolicies[p.policyKey],
                                      retentionDays: e.target.value
                                    }
                                  });
                                }}
                              />
                            </div>

                            {/* Max Delete Batch Size */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <label style={{ fontSize: 10, fontWeight: 700, color: '#475569' }}>Max Delete Batch Size</label>
                              <input
                                type="number"
                                className="metering-input"
                                style={{ width: 85, padding: '3px 6px', height: '28px', fontSize: '12px', borderRadius: '6px' }}
                                value={maxDeleteBatchSize !== undefined ? maxDeleteBatchSize : ''}
                                placeholder="e.g. 5000"
                                onChange={(e) => {
                                  setEditedPolicies({
                                    ...editedPolicies,
                                    [p.policyKey]: {
                                      ...editedPolicies[p.policyKey],
                                      maxDeleteBatchSize: e.target.value
                                    }
                                  });
                                }}
                              />
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              className="metering-btn metering-btn-success"
                              style={{ padding: '4px 10px', fontSize: '11px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}
                              onClick={() => handleSaveRetentionPolicy(p.policyKey, retentionDays, isDryRun, maxDeleteBatchSize)}
                              disabled={savingRetentionPolicy[p.policyKey] || !hasEdits}
                            >
                              {savingRetentionPolicy[p.policyKey] ? <FaSpinner className="metering-spin" /> : <FaCheck />} Save
                            </button>

                            <button
                              className="metering-btn metering-btn-danger"
                              style={{ padding: '4px 10px', fontSize: '11px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}
                              onClick={() => handleRunRetention(p.policyKey)}
                              disabled={executingRetention[p.policyKey]}
                            >
                              {executingRetention[p.policyKey] ? <FaSpinner className="metering-spin" /> : <FaHistory />} Run Purge
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Add Retention Policy Form */}
              <div className="metering-card" style={{ padding: '12px 16px' }}>
                <div className="metering-card-header" style={{ marginBottom: 12 }}>
                  <div className="metering-card-title"><FaPlus /> Add New Data Retention Policy</div>
                </div>
                <form onSubmit={handleCreateRetentionPolicy}>
                  <div className="metering-form-grid" style={{ marginBottom: 8, gap: 12 }}>
                    <div className="metering-field">
                      <label className="metering-label" style={{ fontSize: 10 }}>Policy Key (Unique URL ID)</label>
                      <input
                        type="text"
                        className="metering-input"
                        style={{ height: '30px', padding: '4px 8px', fontSize: '13px', borderRadius: '6px' }}
                        placeholder="e.g. audit_logs"
                        value={newPolicyForm.policyKey}
                        onChange={(e) => setNewPolicyForm({ ...newPolicyForm, policyKey: e.target.value })}
                        required
                      />
                    </div>
                    <div className="metering-field">
                      <label className="metering-label" style={{ fontSize: 10 }}>Policy Name</label>
                      <input
                        type="text"
                        className="metering-input"
                        style={{ height: '30px', padding: '4px 8px', fontSize: '13px', borderRadius: '6px' }}
                        placeholder="e.g. Audit Logs Retention"
                        value={newPolicyForm.name}
                        onChange={(e) => setNewPolicyForm({ ...newPolicyForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="metering-field">
                      <label htmlFor="new_resourceType" className="metering-label" style={{ fontSize: 10 }}>Resource Type</label>
                      <select
                        id="new_resourceType"
                        className="metering-input"
                        style={{ height: '30px', padding: '4px 8px', fontSize: '13px', borderRadius: '6px' }}
                        value={newPolicyForm.resourceType}
                        onChange={(e) => setNewPolicyForm({ ...newPolicyForm, resourceType: e.target.value })}
                        required
                      >
                        <option value="TelemetryEvents">TelemetryEvents</option>
                        <option value="TelemetryAggregates">TelemetryAggregates</option>
                        <option value="ReconciliationReports">ReconciliationReports</option>
                        <option value="UserData">UserData</option>
                      </select>
                    </div>
                  </div>
                  <div className="metering-form-grid" style={{ marginBottom: 12, gap: 12 }}>
                    <div className="metering-field">
                      <label className="metering-label" style={{ fontSize: 10 }}>Retention Window (Days)</label>
                      <input
                        type="number"
                        className="metering-input"
                        style={{ height: '30px', padding: '4px 8px', fontSize: '13px', borderRadius: '6px' }}
                        value={newPolicyForm.retentionDays}
                        onChange={(e) => setNewPolicyForm({ ...newPolicyForm, retentionDays: e.target.value })}
                        required
                      />
                    </div>
                    <div className="metering-field">
                      <label className="metering-label" style={{ fontSize: 10 }}>Max Delete Batch Size</label>
                      <input
                        type="number"
                        className="metering-input"
                        style={{ height: '30px', padding: '4px 8px', fontSize: '13px', borderRadius: '6px' }}
                        value={newPolicyForm.maxDeleteBatchSize}
                        onChange={(e) => setNewPolicyForm({ ...newPolicyForm, maxDeleteBatchSize: e.target.value })}
                        required
                      />
                    </div>
                    <div className="metering-field" style={{ justifyContent: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                        <input
                          type="checkbox"
                          id="new_dryRun"
                          checked={newPolicyForm.dryRun}
                          onChange={(e) => setNewPolicyForm({ ...newPolicyForm, dryRun: e.target.checked })}
                        />
                        <label htmlFor="new_dryRun" style={{ fontSize: 11, fontWeight: 700, color: '#334155', cursor: 'pointer' }}>
                          Dry-run Mode (Preview only)
                        </label>
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="metering-btn metering-btn-primary" style={{ width: '100%', height: '32px', padding: '4px 12px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }} disabled={creatingPolicy}>
                    {creatingPolicy ? <FaSpinner className="metering-spin" /> : <FaPlus />} Create Retention Policy
                  </button>
                </form>
              </div>

              {/* Execution history log */}
              <div className="metering-card" style={{ padding: '12px 16px' }}>
                <div className="metering-card-header" style={{ marginBottom: 12 }}>
                  <div className="metering-card-title"><FaHistory /> Retention Run Audit Trail</div>
                </div>
                <div className="metering-table-container" style={{ maxHeight: 200, overflowY: 'auto' }}>
                  <table className="metering-table compact-audit-table">
                    <thead>
                      <tr>
                        <th>Policy Key</th>
                        <th>Organization</th>
                        <th>Started At</th>
                        <th>Completed At</th>
                        <th>Duration</th>
                        <th>Mode</th>
                        <th style={{ textAlign: 'right' }}>Scanned</th>
                        <th style={{ textAlign: 'right' }}>Deleted</th>
                        <th style={{ textAlign: 'right' }}>Blocked</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!isSystemAdmin ? (
                        <tr>
                          <td colSpan="10" style={{ textAlign: 'center', padding: 20, color: '#64748b', fontSize: '13px' }}>
                            <FaInfoCircle size={16} style={{ marginRight: 8, verticalAlign: 'middle', color: '#4f46e5' }} />
                            Audit trail is only visible to System Administrators.
                          </td>
                        </tr>
                      ) : (!executionHistory || executionHistory.length === 0) ? (
                        <tr><td colSpan="10" style={{ textAlign: 'center', padding: 20 }}>No retention executions recorded.</td></tr>
                      ) : (
                        (executionHistory || []).map(h => {
                          const orgName = organizations.find(o => String(o.id) === String(h.organizationId))?.name;
                          return (
                            <tr key={h.id}>
                              <td><code>{h.policyKey}</code></td>
                              <td>
                                <span title={h.organizationId || 'System level execution'}>
                                  {orgName || (h.organizationId ? `${h.organizationId.substring(0, 8)}...` : 'System')}
                                </span>
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(h.startedAtUtc || h.executedAtUtc)}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(h.completedAtUtc)}</td>
                              <td>{getDuration(h.startedAtUtc || h.executedAtUtc, h.completedAtUtc)}</td>
                              <td>
                                {h.isDryRun || h.status === 'DryRun' ? (
                                  <span className="status-badge draft">Dry Run</span>
                                ) : (
                                  <span className="status-badge active">Active Run</span>
                                )}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{h.itemsScanned ?? 0}</td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: (h.itemsDeleted ?? 0) > 0 ? '#ef4444' : '#64748b' }}>
                                {h.itemsDeleted ?? 0}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: (h.itemsBlockedByLegalHold ?? 0) > 0 ? '#f59e0b' : '#64748b' }}>
                                {h.itemsBlockedByLegalHold ?? 0}
                              </td>
                              <td>
                                {renderRetentionStatus(h.status, h.failureReason)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Legal Holds Controls */}
            <div className="metering-card" style={{ minWidth: 0, padding: '12px 16px' }}>
              <div className="metering-card-header" style={{ marginBottom: 12 }}>
                <div className="metering-card-title"><FaLock /> Legal Hold Protection Registry</div>
              </div>
              {lastHoldResponse && (
                <div className="topology-info-card" style={{ marginBottom: 20, borderLeft: '4px solid #10b981', backgroundColor: '#f0fdf4' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 800, color: '#065f46', fontSize: '11px', textTransform: 'uppercase' }}>
                      New Hold Placed
                    </span>
                    <span className="status-badge active" style={{ backgroundColor: '#dcfce7', color: '#065f46' }}>
                      Created
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#047857' }}>
                    <p style={{ margin: '0 0 4px 0' }}>Scope Key: <b>{lastHoldResponse.scopeKey || '—'}</b></p>
                    <p style={{ margin: 0 }}>Reason: <i>"{lastHoldResponse.reason}"</i></p>
                  </div>
                </div>
              )}

              {legalHoldInfo.isActive && (
                <div className="topology-info-card" style={{ marginBottom: 20, borderLeft: '4px solid #ef4444' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 800 }}>Status: PROTECTED</span>
                    <span className="status-badge retired">
                      Hold Active
                    </span>
                  </div>
                  <div style={{ fontSize: 12, marginTop: 8 }}>
                    <p style={{ margin: '0 0 4px 0' }}>Placed by: <b>{legalHoldInfo.createdBy || legalHoldInfo.placedBy || '—'}</b> ({(() => {
                      const dStr = legalHoldInfo.createdAtUtc || legalHoldInfo.placedAtUtc;
                      if (!dStr) return '—';
                      try {
                        const d = new Date(dStr);
                        return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
                      } catch (e) {
                        return '—';
                      }
                    })()})</p>
                    <p style={{ margin: '0 0 4px 0' }}>Scope Type: <b>{legalHoldInfo.scopeType || '—'}</b></p>
                    <p style={{ margin: '0 0 4px 0' }}>Scope Key: <b>{legalHoldInfo.scopeKey || '—'}</b></p>
                    <p style={{ margin: 0 }}>Reason: <i>"{legalHoldInfo.reason}"</i></p>
                  </div>
                </div>
              )}

              {legalHoldInfo.isActive ? (
                <form onSubmit={handleReleaseLegalHold}>
                  <div className="metering-label" style={{ marginBottom: 12, color: '#b91c1c' }}>Release Legal Hold protection</div>
                  <div className="metering-field" style={{ marginBottom: 12 }}>
                    <label className="metering-label">Released By (Authorizer)</label>
                    <input
                      type="text"
                      className="metering-input"
                      placeholder="e.g. Compliance Officer Name"
                      value={legalHoldReleaseForm.releasedBy}
                      onChange={(e) => setLegalHoldReleaseForm({ ...legalHoldReleaseForm, releasedBy: e.target.value })}
                      required
                    />
                  </div>
                  <div className="metering-field" style={{ marginBottom: 16 }}>
                    <label className="metering-label">Release Reason</label>
                    <textarea
                      className="metering-textarea"
                      placeholder="Document reason for legal hold removal..."
                      value={legalHoldReleaseForm.reason}
                      onChange={(e) => setLegalHoldReleaseForm({ ...legalHoldReleaseForm, reason: e.target.value })}
                      required
                    />
                  </div>
                  <button type="submit" className="metering-btn metering-btn-success" style={{ width: '100%' }}>
                    <FaUnlock /> Release Legal Hold Protection
                  </button>
                </form>
              ) : (
                <form onSubmit={handlePlaceLegalHold}>
                  <div className="metering-label" style={{ marginBottom: 12, color: '#4f46e5' }}>Place Legal Hold protection</div>
                  <div className="metering-field" style={{ marginBottom: 12 }}>
                    <label className="metering-label" style={{ fontSize: 10 }}>Scope Type</label>
                    <select
                      className="metering-input"
                      style={{ height: '30px', padding: '4px 8px', fontSize: '13px', borderRadius: '6px' }}
                      value={legalHoldForm.scopeType}
                      onChange={(e) => setLegalHoldForm({ ...legalHoldForm, scopeType: e.target.value })}
                      required
                    >
                      <option value="organization">organization</option>
                      <option value="tenant">tenant</option>
                      <option value="subscription">subscription</option>
                      <option value="resourceGroup">resourceGroup</option>
                    </select>
                  </div>
                  <div className="metering-field" style={{ marginBottom: 12 }}>
                    <label className="metering-label" style={{ fontSize: 10 }}>Scope Key</label>
                    <input
                      type="text"
                      className="metering-input"
                      style={{ height: '30px', padding: '4px 8px', fontSize: '13px', borderRadius: '6px' }}
                      placeholder="e.g. org-001"
                      value={legalHoldForm.scopeKey}
                      onChange={(e) => setLegalHoldForm({ ...legalHoldForm, scopeKey: e.target.value })}
                      required
                    />
                  </div>
                  <div className="metering-field" style={{ marginBottom: 16 }}>
                    <label className="metering-label" style={{ fontSize: 10 }}>Hold Reason / Scope</label>
                    <textarea
                      className="metering-textarea"
                      style={{ minHeight: '60px', padding: '6px 8px', fontSize: '13px', borderRadius: '6px' }}
                      placeholder="Document compliance hold references..."
                      value={legalHoldForm.reason}
                      onChange={(e) => setLegalHoldForm({ ...legalHoldForm, reason: e.target.value })}
                      required
                    />
                  </div>
                  <button type="submit" className="metering-btn metering-btn-danger" style={{ width: '100%', borderRadius: '6px' }}>
                    <FaLock /> Place Legal Hold Protection
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {activeTab === 'telemetry' && (
          <div className="metering-panel" style={{ animation: 'metering-fadeIn 0.3s ease-out', display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* Top Section: License & User Allocation */}
            {(() => {
              const userLimitObj = entitlements?.limits?.find(l => l.limitKey === 'max_users_per_org' || l.limitKey === 'business_active_users');
              const userUsageObj = usages?.find(u => u.limitKey === 'max_users_per_org' || u.limitKey === 'business_active_users');

              const consumed = userUsageObj ? userUsageObj.consumed : 0;
              const limitValue = userLimitObj ? (userLimitObj.value !== undefined ? userLimitObj.value : (userLimitObj.allowed || 0)) : 0;

              let hardLimit = userLimitObj && userLimitObj.hardLimit !== undefined ? userLimitObj.hardLimit : limitValue;
              const activeTier = tiers?.find(t => t.tierKey === entitlements?.tierKey);
              if (activeTier) {
                const tierLimit = activeTier.limitPolicies?.find(l => l.limitKey === 'max_users_per_org' || l.limitKey === 'business_active_users');
                if (tierLimit && tierLimit.hardLimit !== undefined) {
                  hardLimit = tierLimit.hardLimit;
                }
              }

              const softLimitPercentage = userLimitObj ? userLimitObj.softLimitPercentage : 80;

              const softLimitVal = Math.floor(limitValue * (softLimitPercentage / 100));
              const percentOfHard = hardLimit > 0 ? Math.min(100, Math.floor((consumed / hardLimit) * 100)) : 0;

              const isOverHard = consumed > hardLimit;
              const isOverSoft = consumed > softLimitVal && consumed <= hardLimit;

              let statusBadgeText = 'Healthy';
              let statusBadgeClass = 'active';
              let statusColor = '#10b981';
              let alertBlock = null;

              if (isOverHard) {
                statusBadgeText = 'Limit Exceeded';
                statusBadgeClass = 'retired';
                statusColor = '#ef4444';
                alertBlock = (
                  <div className="enforcement-alert-item danger" style={{ marginTop: 16, padding: '12px 16px', borderRadius: '10px', gap: '10px', marginBottom: 0 }}>
                    <FaExclamationTriangle className="enforcement-alert-icon" style={{ fontSize: '15px', marginTop: '2px' }} />
                    <div className="enforcement-alert-content">
                      <div className="enforcement-alert-title" style={{ fontSize: '13px' }}>Hard Limit Violated</div>
                      <div className="enforcement-alert-desc" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                        Active users ({consumed}) have exceeded the hard limit allocation of {hardLimit}. User provisioning is blocked.
                      </div>
                    </div>
                  </div>
                );
              } else if (isOverSoft) {
                statusBadgeText = 'Warning Zone';
                statusBadgeClass = 'deprecated';
                statusColor = '#f59e0b';
                alertBlock = (
                  <div className="enforcement-alert-item" style={{ marginTop: 16, padding: '12px 16px', borderRadius: '10px', gap: '10px', marginBottom: 0 }}>
                    <FaExclamationTriangle className="enforcement-alert-icon" style={{ fontSize: '15px', marginTop: '2px' }} />
                    <div className="enforcement-alert-content">
                      <div className="enforcement-alert-title" style={{ fontSize: '13px', color: '#78350f' }}>Soft Limit Approached</div>
                      <div className="enforcement-alert-desc" style={{ fontSize: '12px', color: '#92400e', lineHeight: '1.4' }}>
                        Currently in overage warning zone (&gt; {softLimitPercentage}% of plan value). Additional charges may apply.
                      </div>
                    </div>
                  </div>
                );
              } else {
                alertBlock = (
                  <div className="enforcement-alert-item" style={{ marginTop: 16, padding: '12px 16px', borderRadius: '10px', gap: '10px', background: '#ecfdf5', borderColor: '#a7f3d0', marginBottom: 0 }}>
                    <FaCheckCircle className="enforcement-alert-icon" style={{ color: '#10b981', fontSize: '15px', marginTop: '2px' }} />
                    <div className="enforcement-alert-content">
                      <div className="enforcement-alert-title" style={{ fontSize: '13px', color: '#065f46' }}>Allocation Healthy</div>
                      <div className="enforcement-alert-desc" style={{ fontSize: '12px', color: '#047857', lineHeight: '1.4' }}>
                        User count is within safe allocation limits.
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="metering-card license-allocation-card">
                  <div className="metering-card-header">
                    <div className="metering-card-title">
                      <FaIdBadge /> License & User Allocation
                    </div>
                    <span className={`status-badge ${statusBadgeClass}`} style={{ fontSize: '10px', padding: '3px 10px', background: isOverHard ? '#fee2e2' : undefined, color: isOverHard ? '#b91c1c' : undefined }}>
                      {statusBadgeText}
                    </span>
                  </div>

                  {entitlements ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div className="license-stats-grid">
                        <div className="license-stat-card">
                          <span className="license-stat-label">Active Commercial Plan</span>
                          <span className="license-stat-value" style={{ color: '#4f46e5' }}>{entitlements.tierName}</span>
                          <span className="license-stat-sub"><code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{entitlements.tierKey}</code></span>
                        </div>
                        <div className="license-stat-card">
                          <span className="license-stat-label">Seat Allocation</span>
                          <span className="license-stat-value">{limitValue} Users</span>
                          <span className="license-stat-sub">{softLimitPercentage}% Soft Warning</span>
                        </div>
                        <div className="license-stat-card">
                          <span className="license-stat-label">Hard Limit Cap</span>
                          <span className="license-stat-value">{hardLimit} Users</span>
                          <span className="license-stat-sub">Strict Maximum</span>
                        </div>
                        <div className="license-stat-card">
                          <span className="license-stat-label">Active Consumed Seats</span>
                          <span className="license-stat-value" style={{ color: statusColor }}>{consumed} / {hardLimit}</span>
                          <span className="license-stat-sub">{percentOfHard}% Capacity</span>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div style={{ marginTop: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
                          <span>Allocation Usage Bar</span>
                          <span style={{ color: statusColor, fontWeight: 700 }}>{percentOfHard}% Consumed</span>
                        </div>
                        <div style={{ width: '100%', height: '10px', background: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${percentOfHard}%`,
                            height: '100%',
                            background: statusColor,
                            borderRadius: '999px',
                            transition: 'width 0.4s ease'
                          }} />
                        </div>
                      </div>

                      {alertBlock}
                    </div>
                  ) : (
                    <div style={{ padding: '20px 0', fontSize: '14px', color: '#64748b', fontStyle: 'italic', textAlign: 'center' }}>
                      Loading entitlements context...
                    </div>
                  )}
                </div>
              );
            })()}

          </div>
        )}

      </div>
    </div>
  );
}
